/* =========================================================================
   FTFCheap Theme — theme.js
   Vanilla JS. Handles: AJAX cart + drawer, cart goal progress, quick view,
   product forms, buy-now, quantity limits, variant picking, predictive
   search, infinite scroll, hero product floats with color glows, animated
   accordions, image fade-ins, mobile menu, currency selector, marquee.
   ========================================================================= */
(function () {
  'use strict';

  var config = window.FTFTheme || {};
  var routes = config.routes || {
    cart: '/cart',
    cartAdd: '/cart/add',
    cartChange: '/cart/change',
    cartUpdate: '/cart/update',
    predictiveSearch: '/search/suggest',
    search: '/search'
  };

  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var animsOff = document.body ? document.body.classList.contains('no-anims') : false;

  function animMs(name) {
    if (reducedMotion || animsOff) return 0;
    var val = getComputedStyle(document.documentElement).getPropertyValue(name);
    if (!val) return 300;
    val = val.trim();
    if (val.slice(-2) === 'ms') return parseFloat(val);
    return parseFloat(val) * 1000;
  }

  /* ------------------------------------------------------------------
     Utilities
  ------------------------------------------------------------------ */
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || config.moneyFormat || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === 'undefined' ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    var match = formatString.match(placeholderRegex);
    switch (match ? match[1] : 'amount') {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  function fetchConfig(type) {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/' + (type || 'json')
      }
    };
  }

  function setButtonLoading(button, isLoading) {
    if (!button) return;
    var spinner = button.querySelector('.loading-spinner');
    if (spinner) spinner.hidden = !isLoading;
    button.toggleAttribute('aria-busy', isLoading);
    button.disabled = isLoading;
  }

  /* ------------------------------------------------------------------
     Cart state + goal sync + in-cart flags
  ------------------------------------------------------------------ */
  function updateInCartFlags(variantsMap) {
    document.querySelectorAll('[data-in-cart-flag]').forEach(function (flag) {
      var variantId = flag.getAttribute('data-variant-id');
      var qty = variantsMap && variantsMap[variantId] ? variantsMap[variantId] : 0;
      var countEl = flag.querySelector('[data-in-cart-count]');
      if (qty > 0) {
        if (countEl) {
          countEl.textContent = (config.strings && config.strings.inCart ? config.strings.inCart : 'In cart: [count]').replace('[count]', qty);
        }
        flag.hidden = false;
      } else {
        flag.hidden = true;
      }
    });
    document.querySelectorAll('[data-product-card][data-variant-id]').forEach(function (card) {
      var variantId = card.getAttribute('data-variant-id');
      card.classList.toggle('is-in-cart', !!(variantsMap && variantsMap[variantId]));
    });
  }

  function syncCartUI(totalCents, itemCount, variantsMap) {
    document.querySelectorAll('[data-cart-total-display]').forEach(function (el) {
      el.textContent = formatMoney(totalCents);
    });
    document.querySelectorAll('[data-cart-count-display]').forEach(function (el) {
      el.textContent = itemCount;
      el.setAttribute('data-count', itemCount);
    });
    document.querySelectorAll('[data-cart-goal]').forEach(function (goal) {
      var goalCents = parseFloat(goal.getAttribute('data-goal-cents'));
      if (!goalCents || goalCents <= 0) return;
      var progress = Math.min(100, Math.round((totalCents / goalCents) * 100));
      var remaining = Math.max(0, goalCents - totalCents);
      var fill = goal.querySelector('[data-goal-fill]');
      var bar = goal.querySelector('[data-goal-progressbar]');
      if (fill) fill.style.width = progress + '%';
      if (bar) bar.setAttribute('aria-valuenow', progress);
      goal.querySelectorAll('[data-goal-remaining]').forEach(function (el) {
        el.textContent = formatMoney(remaining);
      });
      goal.querySelectorAll('[data-goal-locked]').forEach(function (el) {
        el.hidden = remaining <= 0;
      });
      goal.querySelectorAll('[data-goal-unlocked]').forEach(function (el) {
        el.hidden = remaining > 0;
      });
    });
    if (variantsMap) updateInCartFlags(variantsMap);
  }

  function readCartAttrsAndSync() {
    var root = document.querySelector('[data-cart-root]');
    if (!root) return;
    var total = parseInt(root.getAttribute('data-cart-total'), 10) || 0;
    var count = parseInt(root.getAttribute('data-cart-count'), 10) || 0;
    var variantsMap = {};
    try {
      variantsMap = JSON.parse(root.getAttribute('data-cart-variants') || '{}');
    } catch (e) { variantsMap = {}; }
    syncCartUI(total, count, variantsMap);
  }

  function replaceDrawerFromHTML(html) {
    var drawer = document.getElementById('CartDrawer');
    if (!drawer || !html) return false;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.getElementById('CartDrawer');
    if (!fresh) return false;
    drawer.innerHTML = fresh.innerHTML;
    readCartAttrsAndSync();
    initImageFade(drawer);
    return true;
  }

  function getDrawerSectionId() {
    var drawerSection = document.querySelector('[data-cart-drawer-section]');
    return drawerSection ? drawerSection.getAttribute('data-cart-drawer-section') : null;
  }

  function refreshCartDrawer() {
    var sectionId = getDrawerSectionId();
    if (!sectionId) return Promise.resolve(false);
    var url = window.location.pathname + (window.location.pathname.indexOf('?') === -1 ? '?' : '&') + 'sections=' + sectionId;
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        return replaceDrawerFromHTML(sections[sectionId]);
      })
      .catch(function (err) {
        if (window.console && console.warn) console.warn('FTF: drawer refresh failed', err);
        return false;
      });
  }

  function showCartError(message) {
    var errorEl = document.querySelector('[data-cart-error]');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
      setTimeout(function () { errorEl.hidden = true; }, 6000);
    } else {
      window.alert(message);
    }
  }

  /* ------------------------------------------------------------------
     Cart drawer open / close
  ------------------------------------------------------------------ */
  var lastFocused = null;

  function getDrawer() {
    return document.getElementById('CartDrawerWrapper');
  }

  function openCartDrawer() {
    var d = getDrawer();
    if (!d) {
      window.location.href = routes.cart;
      return;
    }
    lastFocused = document.activeElement;
    d.classList.add('is-open');
    d.removeAttribute('aria-hidden');
    document.body.classList.add('overflow-hidden');
    var panel = d.querySelector('.cart-drawer__panel');
    if (panel) panel.focus({ preventScroll: true });
  }

  function closeCartDrawer() {
    var d = getDrawer();
    if (!d || !d.classList.contains('is-open')) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  /* ------------------------------------------------------------------
     Header search panel + copy-to-clipboard help button
  ------------------------------------------------------------------ */
  function closeHeaderSearch() {
    var panel = document.getElementById('HeaderSearch');
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    var toggle = document.querySelector('[data-header-search-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  function legacyCopy(text) {
    var area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.top = '-100vh';
    document.body.appendChild(area);
    area.select();
    try { document.execCommand('copy'); } catch (e) { /* unsupported */ }
    document.body.removeChild(area);
  }

  function copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).catch(function () { legacyCopy(text); });
    }
    legacyCopy(text);
    return Promise.resolve();
  }

  /* ------------------------------------------------------------------
     Collection tiles: random floating item previews, reshuffled per load
  ------------------------------------------------------------------ */
  function initCollectionTiles() {
    document.querySelectorAll('[data-collection-tile]').forEach(function (tile) {
      if (tile.getAttribute('data-ctile-init')) return;
      tile.setAttribute('data-ctile-init', 'true');
      var poolEl = tile.querySelector('[data-ctile-pool]');
      var holder = tile.querySelector('.ctile__floats');
      if (!poolEl || !holder) return;
      var pool = [];
      try { pool = JSON.parse(poolEl.textContent); } catch (e) { pool = []; }
      if (!pool.length) return;
      var count = parseInt(tile.getAttribute('data-ctile-items') || '4', 10);
      for (var i = pool.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var tmp = pool[i]; pool[i] = pool[j]; pool[j] = tmp;
      }
      var slots = ['a', 'b', 'c', 'd', 'e', 'f'];
      var max = Math.min(count, pool.length, slots.length);
      var inModal = !!tile.closest('.vr-modal');
      for (var k = 0; k < max; k++) {
        var img = document.createElement('img');
        img.className = 'ctile__item ctile__item--' + slots[k];
        img.alt = '';
        img.loading = inModal ? 'eager' : 'lazy';
        img.setAttribute('data-fade-bound', 'true');
        img.style.setProperty('--ct-rot', (Math.random() * 24 - 12).toFixed(1) + 'deg');
        img.style.setProperty('--ct-delay', (k * 0.08) + 's');
        img.crossOrigin = 'anonymous'; // before src, so tile colors can be sampled
        img.src = pool[k].src;
        holder.appendChild(img);
      }
    });
  }

  /* ------------------------------------------------------------------
     US-only gate: browser-timezone heuristic, no external services.
     Courtesy layer only - real enforcement is the merchant's Shopify
     Flow rule cancelling non-US orders.
  ------------------------------------------------------------------ */
  var US_TIMEZONES = [
    'America/New_York', 'America/Detroit', 'America/Kentucky/Louisville', 'America/Kentucky/Monticello',
    'America/Indiana/Indianapolis', 'America/Indiana/Vincennes', 'America/Indiana/Winamac',
    'America/Indiana/Marengo', 'America/Indiana/Petersburg', 'America/Indiana/Vevay',
    'America/Indiana/Tell_City', 'America/Indiana/Knox', 'America/Chicago', 'America/Menominee',
    'America/North_Dakota/Center', 'America/North_Dakota/New_Salem', 'America/North_Dakota/Beulah',
    'America/Denver', 'America/Boise', 'America/Phoenix', 'America/Los_Angeles',
    'America/Anchorage', 'America/Juneau', 'America/Sitka', 'America/Metlakatla',
    'America/Yakutat', 'America/Nome', 'America/Adak', 'Pacific/Honolulu',
    // US territories
    'America/Puerto_Rico', 'America/St_Thomas', 'America/Virgin', 'Pacific/Guam', 'Pacific/Saipan', 'Pacific/Pago_Pago',
    // legacy aliases some devices still report
    'US/Eastern', 'US/Central', 'US/Mountain', 'US/Pacific', 'US/Alaska', 'US/Hawaii', 'US/Arizona',
    'US/Michigan', 'US/East-Indiana', 'US/Indiana-Starke', 'US/Aleutian', 'US/Samoa',
    'America/Indianapolis', 'America/Fort_Wayne', 'America/Louisville', 'America/Knox_IN',
    'America/Shiprock', 'America/Atka', 'Navajo', 'Pacific/Johnston'
  ];

  function isLikelyUS() {
    try {
      var tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      if (!tz) return true; // unknown: never lock someone out on a guess
      return US_TIMEZONES.indexOf(tz) !== -1;
    } catch (e) {
      return true;
    }
  }

  function initGeoGate() {
    var gate = document.getElementById('GeoGate');
    if (!gate || gate.getAttribute('data-geo-init')) return;
    gate.setAttribute('data-geo-init', 'true');
    try {
      if (sessionStorage.getItem('ftf-geo-override') === '1') return;
    } catch (e) { /* private mode */ }
    if (isLikelyUS()) return;
    gate.classList.add('is-open');
    gate.removeAttribute('aria-hidden');
    document.body.classList.add('overflow-hidden');
    document.body.classList.add('geo-blocked');
    var panel = gate.querySelector('.geo-gate__panel');
    if (panel) {
      try { panel.focus({ preventScroll: true }); } catch (e) { panel.focus(); }
    }
  }

  function dismissGeoGate() {
    var gate = document.getElementById('GeoGate');
    if (!gate) return;
    try { sessionStorage.setItem('ftf-geo-override', '1'); } catch (e) { /* private mode */ }
    gate.classList.remove('is-open');
    gate.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('geo-blocked');
  }

  /* ------------------------------------------------------------------
     Quick-nav modal tiles: blend each tile's background gradient from
     the colors of its randomly chosen items
  ------------------------------------------------------------------ */
  function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    var max = Math.max(r, g, b);
    var min = Math.min(r, g, b);
    var h = 0;
    var s = 0;
    var l = (max + min) / 2;
    if (max !== min) {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h /= 6;
    }
    return [h * 360, s, l];
  }

  function sampleImageColor(img) {
    try {
      var size = 24;
      var canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, size, size);
      var d = ctx.getImageData(0, 0, size, size).data;
      var r = 0, g = 0, b = 0, n = 0;
      for (var i = 0; i < d.length; i += 4) {
        if (d[i + 3] < 120) continue;
        var mx = Math.max(d[i], d[i + 1], d[i + 2]);
        var mn = Math.min(d[i], d[i + 1], d[i + 2]);
        if (mx > 235 && mx - mn < 24) continue; // near-white
        if (mx < 40) continue; // near-black
        if (mx - mn < 18) continue; // grey
        r += d[i]; g += d[i + 1]; b += d[i + 2]; n++;
      }
      if (n < 10) return null;
      var hsl = rgbToHsl(r / n, g / n, b / n);
      // clamp into a pleasant band so every tile reads well
      return {
        h: Math.round(hsl[0]),
        s: Math.min(Math.max(hsl[1], 0.5), 0.72),
        l: Math.min(Math.max(hsl[2], 0.44), 0.6)
      };
    } catch (e) {
      return null; // tainted canvas or similar: keep the fallback gradient
    }
  }

  function initCnavAutoColors() {
    document.querySelectorAll('[data-cnav-auto] [data-collection-tile]').forEach(function (tile) {
      if (tile.getAttribute('data-cnav-colored')) return;
      tile.setAttribute('data-cnav-colored', 'true');
      var recompute = function () {
        var colors = [];
        tile.querySelectorAll('.ctile__item').forEach(function (img) {
          if (!img.complete || !img.naturalWidth) return;
          var c = sampleImageColor(img);
          if (c) colors.push(c);
        });
        if (colors.length < 2) return;
        colors.sort(function (a, b) { return a.h - b.h; });
        var stops = colors.map(function (c, i) {
          var pos = Math.round(i * 100 / colors.length);
          return 'hsl(' + c.h + ', ' + Math.round(c.s * 100) + '%, ' + Math.round(c.l * 100) + '%) ' + pos + '%';
        });
        var last = colors[colors.length - 1];
        stops.push('hsl(' + last.h + ', ' + Math.round(last.s * 100) + '%, ' + Math.round(Math.max(0.3, last.l - 0.14) * 100) + '%) 100%');
        tile.style.setProperty('--ct-bg', 'linear-gradient(130deg, ' + stops.join(', ') + ')');
      };
      tile.querySelectorAll('.ctile__item').forEach(function (img) {
        if (!img.complete) img.addEventListener('load', recompute, { once: true });
      });
      recompute();
    });
  }

  /* ------------------------------------------------------------------
     Sale countdown timers: compact card timers (product tag
     sale-until:YYYY-MM-DD) and the big-chip sale banner (data-sale-clock)
  ------------------------------------------------------------------ */
  var saleTimersStarted = false;

  function parseSaleUntil(value) {
    var v = String(value || '').trim();
    if (!v) return null;
    var d;
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
      // date-only: the sale runs through the end of that day, local time
      var p = v.split('-');
      d = new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10), 23, 59, 59);
    } else {
      d = new Date(v);
    }
    return isNaN(d.getTime()) ? null : d;
  }

  function initSaleTimers() {
    if (saleTimersStarted) return;
    saleTimersStarted = true;
    var pad = function (n) { return n < 10 ? '0' + n : String(n); };
    var setNum = function (el, sel, val) {
      var n = el.querySelector(sel);
      if (n && n.textContent !== val) n.textContent = val;
    };
    var setClock = function (el, d, h, m, s) {
      var dayChip = el.querySelector('[data-clock-days]');
      if (dayChip) dayChip.hidden = d <= 0;
      setNum(el, '[data-clock-d]', String(d));
      setNum(el, '[data-clock-h]', pad(h));
      setNum(el, '[data-clock-m]', pad(m));
      setNum(el, '[data-clock-s]', pad(s));
    };
    var hideSaleBanner = function (el) {
      var target = el.closest('.shopify-section') || el;
      target.style.display = 'none';
    };
    var tick = function () {
      // re-query every tick so cards added later (infinite scroll,
      // quick view, recommendations) get counted down too
      var els = document.querySelectorAll('[data-sale-until]:not([data-sale-done])');
      if (!els.length) return;
      var now = Date.now();
      els.forEach(function (el) {
        var until = parseSaleUntil(el.getAttribute('data-sale-until'));
        var isClock = el.hasAttribute('data-sale-clock');
        var out = isClock ? null : el.querySelector('[data-sale-timer-text]');
        if (!until || (!isClock && !out)) {
          el.setAttribute('data-sale-done', 'true');
          if (isClock) {
            // banner with no/invalid date: zeros in the editor, gone live
            if (el.hasAttribute('data-sale-preview')) setClock(el, 0, 0, 0, 0);
            else hideSaleBanner(el);
          }
          return;
        }
        var diff = until.getTime() - now;
        if (diff <= 0) {
          el.setAttribute('data-sale-done', 'true');
          if (isClock) {
            if (el.hasAttribute('data-sale-preview')) {
              setClock(el, 0, 0, 0, 0);
              el.classList.add('is-over');
            } else {
              hideSaleBanner(el);
            }
            return;
          }
          el.hidden = true;
          var card = el.closest('.product-card');
          if (card) card.classList.remove('product-card--glow');
          return;
        }
        var days = Math.floor(diff / 86400000);
        var hours = Math.floor(diff % 86400000 / 3600000);
        var mins = Math.floor(diff % 3600000 / 60000);
        var secs = Math.floor(diff % 60000 / 1000);
        if (isClock) {
          setClock(el, days, hours, mins, secs);
          return;
        }
        var text = (days > 0 ? days + 'd ' : '') + pad(hours) + ':' + pad(mins) + ':' + pad(secs);
        if (out.textContent !== text) out.textContent = text;
        if (el.hidden) el.hidden = false;
      });
    };
    tick();
    setInterval(tick, 1000);
  }

  /* ------------------------------------------------------------------
     Discount links: /discount/CODE visitors see discounted prices,
     per-item savings and a welcome toast. The storefront is never told
     what a code does, so the merchant lists shareable codes as
     CODE:PERCENT in theme settings and we mirror the math for display.
  ------------------------------------------------------------------ */
  var activeDiscount = null;
  var discountObserverStarted = false;

  function parseDiscountRules(raw) {
    var rules = {};
    String(raw || '').split(/\r?\n/).forEach(function (line) {
      var parts = line.split(':');
      if (parts.length < 2) return;
      var code = parts[0].trim().toUpperCase();
      var pct = parseFloat(String(parts[1]).replace('%', ''));
      if (code && isFinite(pct) && pct > 0 && pct <= 100) rules[code] = pct;
    });
    return rules;
  }

  function readDiscountCode() {
    var param = null;
    try { param = new URLSearchParams(window.location.search).get('discount'); } catch (e) { /* no support */ }
    if (param) return param.trim();
    var match = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/);
    if (!match) return null;
    try { return decodeURIComponent(match[1]).trim(); } catch (e) { return match[1].trim(); }
  }

  function applyDiscountPricing() {
    if (!activeDiscount) return;
    var cfg = config.discountLinks || {};
    var saveTemplate = cfg.saveText || 'Save {amount}';
    var showSavings = cfg.showSavings !== false;
    document.querySelectorAll('.price__current[data-price-cents]').forEach(function (el) {
      // cart totals never reflect the code before checkout, so leave cart UI alone
      if (el.closest('.cart-drawer, [data-cart-root], .cart-page')) return;
      var cents = parseInt(el.getAttribute('data-price-cents'), 10);
      if (!isFinite(cents) || cents <= 0) return;
      if (el.getAttribute('data-discount-base') === String(cents)) return;
      var discounted = Math.round(cents * (100 - activeDiscount.percent) / 100);
      var saved = cents - discounted;
      if (saved <= 0) return;
      el.setAttribute('data-discount-base', String(cents));
      el.textContent = formatCatalogMoney(discounted);
      var parent = el.parentElement;
      if (!parent) return;
      var extras = parent.querySelector('.discount-extras');
      if (extras) extras.remove();
      extras = document.createElement('span');
      extras.className = 'discount-extras';
      if (!parent.querySelector('.price__compare:not([hidden])')) {
        var was = document.createElement('s');
        was.className = 'price__was';
        was.textContent = formatCatalogMoney(cents);
        extras.appendChild(was);
      }
      if (showSavings) {
        var flag = document.createElement('span');
        flag.className = 'discount-save-flag';
        flag.textContent = saveTemplate.replace('{amount}', formatCatalogMoney(saved));
        extras.appendChild(flag);
      }
      if (extras.childNodes.length) el.insertAdjacentElement('afterend', extras);
    });
  }

  function resetDiscountPricing() {
    document.querySelectorAll('[data-discount-base]').forEach(function (el) {
      var cents = parseInt(el.getAttribute('data-price-cents'), 10);
      el.removeAttribute('data-discount-base');
      el.removeAttribute('data-cur-state');
      if (isFinite(cents)) el.textContent = formatCatalogMoney(cents);
      var parent = el.parentElement;
      var extras = parent && parent.querySelector('.discount-extras');
      if (extras) extras.remove();
    });
  }

  function updateDrawerDiscountNote() {
    if (!activeDiscount) return;
    var status = document.querySelector('[data-promocode-status]');
    if (!status) return;
    var text = ((config.strings || {}).promoAuto || 'Code {code} will be applied at checkout.').replace('{code}', activeDiscount.code);
    // never overwrite a message the promo field just showed
    if (!status.hidden && status.textContent.trim() !== '' && status.textContent !== text) return;
    if (status.textContent !== text) status.textContent = text;
    if (status.hidden) status.hidden = false;
  }

  function refreshDiscountState() {
    var cfg = config.discountLinks || {};
    var rules = parseDiscountRules(cfg.rules);
    var code = readDiscountCode();
    var norm = code ? code.toUpperCase() : null;
    var pct = norm ? rules[norm] : null;
    if (activeDiscount && norm !== activeDiscount.code) {
      resetDiscountPricing();
      activeDiscount = null;
    }
    if (!activeDiscount && norm && pct) {
      activeDiscount = { code: norm, percent: pct };
    }
    if (activeDiscount) {
      applyDiscountPricing();
      updateDrawerDiscountNote();
    }
  }

  function showDiscountToast() {
    if (!activeDiscount) return;
    var key = 'ftf-discount-toast-' + activeDiscount.code;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch (e) { /* private mode: show every visit */ }
    var cfg = config.discountLinks || {};
    var toast = document.createElement('div');
    toast.className = 'discount-toast';
    toast.setAttribute('role', 'status');
    var icon = document.createElement('span');
    icon.className = 'discount-toast__icon';
    icon.innerHTML = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m4.5 12.5 5 5 10-11"/></svg>';
    var chip = document.createElement('span');
    chip.className = 'discount-toast__code';
    chip.textContent = activeDiscount.code;
    var body = document.createElement('span');
    body.className = 'discount-toast__text';
    body.textContent = (cfg.popupText || 'Your {percent}% discount is applied automatically at checkout!')
      .replace('{percent}', String(activeDiscount.percent))
      .replace('{code}', activeDiscount.code);
    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'discount-toast__close';
    close.setAttribute('aria-label', ((config.strings || {}).close) || 'Close');
    close.innerHTML = '&times;';
    toast.appendChild(icon);
    toast.appendChild(chip);
    toast.appendChild(body);
    toast.appendChild(close);
    document.body.appendChild(toast);
    var hide = function () {
      toast.classList.add('is-leaving');
      setTimeout(function () { toast.remove(); }, 450);
    };
    close.addEventListener('click', hide);
    setTimeout(hide, 9000);
  }

  function initDiscountLinks() {
    var cfg = config.discountLinks || {};
    if (cfg.enabled === false) return;
    refreshDiscountState();
    if (!activeDiscount) return;
    showDiscountToast();
    if (!discountObserverStarted) {
      discountObserverStarted = true;
      // injected content (infinite scroll, quick view, recommendations,
      // drawer re-renders, variant price updates) gets re-priced automatically
      var refresh = debounce(refreshDiscountState, 120);
      try {
        new MutationObserver(refresh).observe(document.body, { childList: true, subtree: true, characterData: true });
      } catch (e) { /* initial pass only */ }
    }
  }

  /* ------------------------------------------------------------------
     Currency display: browsing-only conversion for stores without
     Shopify Payments multi-currency. Catalog prices convert on screen
     from a merchant-set (or auto-refreshed) rate table; the cart and
     checkout always stay in the store currency (USD).
  ------------------------------------------------------------------ */
  var displayCurrency = null;
  var currencyOptions = [];
  var currencyRates = {};
  var currencyObserverStarted = false;

  function flagEmoji(iso) {
    iso = String(iso || '').toUpperCase();
    if (iso.length !== 2) return '';
    return String.fromCodePoint(iso.charCodeAt(0) + 127397, iso.charCodeAt(1) + 127397);
  }

  function parseCurrencyList(raw) {
    var out = [];
    String(raw || '').split(/\r?\n/).forEach(function (line) {
      var parts = line.split(':');
      if (parts.length < 2) return;
      var flag = parts[0].trim().toUpperCase();
      var code = parts[1].trim().toUpperCase();
      var label = (parts[2] || code).trim();
      if (flag.length === 2 && /^[A-Z]{3}$/.test(code)) out.push({ flag: flag, code: code, label: label });
    });
    return out;
  }

  function parseCurrencyRates(raw) {
    var out = {};
    String(raw || '').split(/\r?\n/).forEach(function (line) {
      var parts = line.split(':');
      if (parts.length < 2) return;
      var code = parts[0].trim().toUpperCase();
      var rate = parseFloat(parts[1]);
      if (/^[A-Z]{3}$/.test(code) && isFinite(rate) && rate > 0) out[code] = rate;
    });
    out.USD = 1;
    return out;
  }

  function formatConvertedMoney(cents) {
    var val = cents / 100 * displayCurrency.rate;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: displayCurrency.code }).format(val);
    } catch (e) {
      return displayCurrency.code + ' ' + val.toFixed(2);
    }
  }

  // catalog prices follow the picked display currency; cart math stays USD
  function formatCatalogMoney(cents, format) {
    if (displayCurrency) return formatConvertedMoney(cents);
    return formatMoney(cents, format);
  }

  function applyCurrencyDisplay() {
    if (!displayCurrency) return;
    var state = displayCurrency.code + '@' + displayCurrency.rate;
    document.querySelectorAll('[data-price-cents]').forEach(function (el) {
      if (el.closest('.cart-drawer, [data-cart-root], .cart-page')) return;
      var cents = parseInt(el.getAttribute('data-price-cents'), 10);
      if (!isFinite(cents) || cents <= 0) return;
      var eff = cents;
      if (activeDiscount && el.hasAttribute('data-discount-base')) {
        eff = Math.round(cents * (100 - activeDiscount.percent) / 100);
      }
      var want = state + ':' + eff;
      if (el.getAttribute('data-cur-state') === want) return;
      el.setAttribute('data-cur-state', want);
      el.textContent = formatConvertedMoney(eff);
    });
  }

  function restoreUsdDisplay() {
    document.querySelectorAll('[data-cur-state]').forEach(function (el) {
      el.removeAttribute('data-cur-state');
      var cents = parseInt(el.getAttribute('data-price-cents'), 10);
      if (!isFinite(cents) || cents <= 0) return;
      var eff = cents;
      if (activeDiscount && el.hasAttribute('data-discount-base')) {
        eff = Math.round(cents * (100 - activeDiscount.percent) / 100);
      }
      el.textContent = formatMoney(eff);
    });
  }

  function updateCurrencyPills() {
    var code = displayCurrency ? displayCurrency.code : 'USD';
    var flag = 'US';
    for (var i = 0; i < currencyOptions.length; i++) {
      if (currencyOptions[i].code === code) { flag = currencyOptions[i].flag; break; }
    }
    document.querySelectorAll('[data-currency-display]').forEach(function (wrap) {
      var flagEl = wrap.querySelector('[data-cur-flag]');
      var codeEl = wrap.querySelector('[data-cur-code]');
      if (flagEl) flagEl.textContent = flagEmoji(flag);
      if (codeEl && codeEl.textContent !== code) codeEl.textContent = code;
      wrap.querySelectorAll('[data-cur-pick]').forEach(function (btn) {
        btn.classList.toggle('is-active', btn.getAttribute('data-cur-pick') === code);
      });
    });
  }

  function buildCurrencyOptions() {
    document.querySelectorAll('[data-currency-display] [data-cur-options]').forEach(function (listEl) {
      listEl.innerHTML = '';
      currencyOptions.forEach(function (opt) {
        if (opt.code !== 'USD' && !currencyRates[opt.code]) return;
        var li = document.createElement('li');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'currency-list__item';
        btn.setAttribute('data-cur-pick', opt.code);
        var flag = document.createElement('span');
        flag.className = 'currency-list__flag';
        flag.setAttribute('aria-hidden', 'true');
        flag.textContent = flagEmoji(opt.flag);
        var name = document.createElement('span');
        name.className = 'currency-list__name';
        name.textContent = opt.label;
        var code = document.createElement('span');
        code.className = 'currency-list__code';
        code.textContent = opt.code;
        btn.appendChild(flag);
        btn.appendChild(name);
        btn.appendChild(code);
        li.appendChild(btn);
        listEl.appendChild(li);
      });
    });
  }

  function startCurrencyObserver() {
    if (currencyObserverStarted) return;
    currencyObserverStarted = true;
    var refresh = debounce(applyCurrencyDisplay, 120);
    try {
      new MutationObserver(refresh).observe(document.body, { childList: true, subtree: true, characterData: true });
    } catch (e) { /* initial pass only */ }
  }

  function setDisplayCurrency(code) {
    var next = null;
    if (code && code !== 'USD') {
      var rate = currencyRates[code];
      if (!isFinite(rate) || rate <= 0) return;
      next = { code: code, rate: rate };
    }
    displayCurrency = next;
    try {
      if (next) localStorage.setItem('ftf-display-currency', next.code);
      else localStorage.removeItem('ftf-display-currency');
    } catch (e) { /* private mode */ }
    if (!next) restoreUsdDisplay();
    if (activeDiscount) {
      // rebuild the discounted price + extras in the new currency
      resetDiscountPricing();
      refreshDiscountState();
    }
    applyCurrencyDisplay();
    updateCurrencyPills();
    if (next) startCurrencyObserver();
  }

  function mergeCurrencyRates(fresh) {
    Object.keys(fresh).forEach(function (code) {
      var rate = parseFloat(fresh[code]);
      if (isFinite(rate) && rate > 0) currencyRates[code.toUpperCase()] = rate;
    });
    currencyRates.USD = 1;
    buildCurrencyOptions();
    if (displayCurrency && currencyRates[displayCurrency.code] !== displayCurrency.rate) {
      displayCurrency = { code: displayCurrency.code, rate: currencyRates[displayCurrency.code] };
      if (activeDiscount) {
        resetDiscountPricing();
        refreshDiscountState();
      }
      applyCurrencyDisplay();
    }
    updateCurrencyPills();
  }

  function refreshCurrencyRates(codes) {
    var cached = null;
    try { cached = JSON.parse(localStorage.getItem('ftf-fx') || 'null'); } catch (e) { /* corrupt */ }
    if (cached && cached.rates && cached.t && Date.now() - cached.t < 43200000) {
      mergeCurrencyRates(cached.rates);
      return;
    }
    var want = codes.filter(function (c) { return c !== 'USD'; });
    if (!want.length || typeof fetch !== 'function') return;
    fetch('https://api.frankfurter.app/latest?base=USD&symbols=' + want.join(','))
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.rates) return;
        try { localStorage.setItem('ftf-fx', JSON.stringify({ t: Date.now(), rates: data.rates })); } catch (e) { /* full */ }
        mergeCurrencyRates(data.rates);
      })
      .catch(function () { /* offline or blocked: manual rates still apply */ });
  }

  function initCurrencyDisplay() {
    var cfg = config.currencyDisplay || {};
    if (cfg.enabled === false) return;
    if (!document.querySelector('[data-currency-display]')) return;
    // baked-in defaults mirror the theme settings so a stale layout
    // config can't silently disable the selector
    var listRaw = cfg.list || 'US:USD:United States\nCA:CAD:Canada\nGB:GBP:United Kingdom\nEU:EUR:Europe\nAU:AUD:Australia\nMX:MXN:Mexico\nBR:BRL:Brazil\nPH:PHP:Philippines\nJP:JPY:Japan';
    var ratesRaw = cfg.rates || 'EUR:0.86\nGBP:0.75\nCAD:1.37\nAUD:1.52\nMXN:18.6\nBRL:5.4\nPHP:57\nJPY:147';
    currencyOptions = parseCurrencyList(listRaw);
    if (!currencyOptions.length) return;
    currencyRates = parseCurrencyRates(ratesRaw);
    buildCurrencyOptions();
    var saved = null;
    try { saved = localStorage.getItem('ftf-display-currency'); } catch (e) { /* private mode */ }
    var valid = saved && saved !== 'USD' && currencyRates[saved] &&
      currencyOptions.some(function (o) { return o.code === saved; });
    if (valid) setDisplayCurrency(saved);
    else updateCurrencyPills();
    if (cfg.auto !== false) refreshCurrencyRates(currencyOptions.map(function (o) { return o.code; }));
  }

  /* ------------------------------------------------------------------
     Item value badges: community trading values from the merchant's
     FTF Values site (base44 public API), shown as a small gem badge on
     product cards. Auto-matches by title; a value:Item Name product
     tag forces the match. Cached in the visitor's browser for 6h.
  ------------------------------------------------------------------ */
  var itemValuesState = null;
  var valueObserverStarted = false;

  function normValueName(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  function baseValueName(s) {
    // "Star Wrap Bundle" and "Star Wrap Duo" share the base "starwrap"
    return normValueName(String(s || '').replace(/\b(set|bundle|duo|trio|pack)\b/gi, ' '));
  }

  function formatValueNumber(v) {
    return String(Math.round(v * 10) / 10);
  }

  function setupItemValues(items) {
    var byNorm = {};
    var baseLists = {};
    items.forEach(function (it) {
      var key = normValueName(it.name);
      if (!key) return;
      byNorm[key] = it;
      var b = baseValueName(it.name);
      if (b.length >= 4) (baseLists[b] = baseLists[b] || []).push(it);
    });
    var byBase = {};
    Object.keys(baseLists).forEach(function (b) {
      // only unambiguous bases may match
      if (baseLists[b].length === 1) byBase[b] = baseLists[b][0];
    });
    var names = Object.keys(byNorm).sort(function (a, b) { return b.length - a.length; });
    if (!names.length) return;
    itemValuesState = { byNorm: byNorm, byBase: byBase, names: names };
    applyItemValues();
    if (!valueObserverStarted) {
      valueObserverStarted = true;
      var refresh = debounce(applyItemValues, 150);
      try {
        new MutationObserver(refresh).observe(document.body, { childList: true, subtree: true });
      } catch (e) { /* initial pass only */ }
    }
  }

  function findItemValue(card) {
    var forced = card.getAttribute('data-value-item');
    if (forced) {
      var f = itemValuesState.byNorm[normValueName(forced)];
      if (f) return f;
    }
    var titleEl = card.querySelector('.product-card__title');
    var title = card.getAttribute('data-value-title') || (titleEl ? titleEl.textContent : '');
    var t = normValueName(title);
    if (!t) return null;
    if (itemValuesState.byNorm[t]) return itemValuesState.byNorm[t];
    var b = baseValueName(title);
    if (b && itemValuesState.byBase[b]) return itemValuesState.byBase[b];
    // longest value-item name contained in the product title wins
    for (var i = 0; i < itemValuesState.names.length; i++) {
      var n = itemValuesState.names[i];
      if (n.length >= 4 && t.indexOf(n) !== -1) return itemValuesState.byNorm[n];
    }
    return null;
  }

  function applyItemValues() {
    if (!itemValuesState) return;
    var label = ((config.itemValues || {}).label) || 'Value';
    document.querySelectorAll('.product-card:not([data-value-done])').forEach(function (card) {
      card.setAttribute('data-value-done', 'true');
      var it = findItemValue(card);
      if (!it) return;
      var media = card.querySelector('.product-card__media') || card;
      var note = document.createElement('span');
      note.className = 'value-note';
      var tip = label + ': ' + formatValueNumber(it.value);
      if (isFinite(it.min) && isFinite(it.max) && it.max > 0) {
        tip += ' (' + formatValueNumber(it.min) + '–' + formatValueNumber(it.max) + ')';
      }
      note.title = tip;
      note.setAttribute('aria-label', tip);
      var lab = document.createElement('span');
      lab.className = 'value-note__label';
      lab.textContent = label;
      var num = document.createElement('span');
      num.className = 'value-note__num';
      num.textContent = formatValueNumber(it.value);
      note.appendChild(lab);
      note.appendChild(num);
      media.appendChild(note);
    });
  }

  function initItemValues() {
    var cfg = config.itemValues || {};
    // opt-in: requires the theme setting to be on (no baked default)
    if (cfg.enabled !== true || !cfg.url) return;
    var valuesUrl = cfg.url;
    var cached = null;
    try { cached = JSON.parse(localStorage.getItem('ftf-values2') || 'null'); } catch (e) { /* corrupt */ }
    if (cached && cached.t && Date.now() - cached.t < 21600000 && Array.isArray(cached.items)) {
      setupItemValues(cached.items);
      return;
    }
    if (typeof fetch !== 'function') return;
    // single items + the set/bundle list (total values) from the same app
    var setsUrl = valuesUrl.replace(/\/entities\/Item$/, '/entities/ItemSet');
    var grab = function (url) {
      return fetch(url)
        .then(function (r) { return r && r.ok ? r.json() : null; })
        .catch(function () { return null; });
    };
    Promise.all([grab(valuesUrl), setsUrl !== valuesUrl ? grab(setsUrl) : null])
      .then(function (results) {
        var items = [];
        (Array.isArray(results[0]) ? results[0] : []).forEach(function (i) {
          if (!i || !i.name || i.is_na) return;
          var v = parseFloat(i.value);
          if (!isFinite(v)) return;
          items.push({
            name: i.name,
            value: v,
            min: parseFloat(i.range_min),
            max: parseFloat(i.range_max)
          });
        });
        (Array.isArray(results[1]) ? results[1] : []).forEach(function (i) {
          if (!i || !i.name) return;
          var v = parseFloat(i.total_value);
          if (!isFinite(v)) return;
          items.push({ name: i.name, value: v, min: NaN, max: NaN });
        });
        if (!items.length) return;
        try { localStorage.setItem('ftf-values2', JSON.stringify({ t: Date.now(), items: items })); } catch (e) { /* full */ }
        setupItemValues(items);
      })
      .catch(function () { /* values site unreachable: no badges this visit */ });
  }

  /* ------------------------------------------------------------------
     Video reward modal (order number -> ad opportunity)
  ------------------------------------------------------------------ */
  function openVideoReward(selector) {
    var modal = selector ? document.querySelector(selector) : document.querySelector('[data-vr-modal]');
    if (!modal) return;
    lastFocused = document.activeElement;
    // fresh start on every open
    modal.querySelectorAll('[data-vr-step]').forEach(function (step) {
      step.hidden = step.getAttribute('data-vr-step') !== 'form';
    });
    var status = modal.querySelector('[data-vr-status]');
    if (status) status.hidden = true;
    modal.classList.add('is-open');
    modal.removeAttribute('aria-hidden');
    document.body.classList.add('overflow-hidden');
    var panel = modal.querySelector('.vr-modal__panel');
    if (panel) {
      try { panel.focus({ preventScroll: true }); } catch (e) { panel.focus(); }
    }
  }

  function closeVideoReward() {
    var modal = document.querySelector('.vr-modal.is-open');
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  function normalizeOrderRef(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  function handleVideoRewardSubmit(form) {
    var modal = form.closest('[data-vr-modal]');
    if (!modal) return;
    var status = modal.querySelector('[data-vr-status]');
    var showErr = function (msg) {
      if (status) { status.textContent = msg; status.hidden = false; }
    };
    if (status) status.hidden = true;

    var orderRaw = (form.querySelector('[name="vr_order"]') || {}).value || '';
    orderRaw = orderRaw.trim();
    var email = ((form.querySelector('[name="vr_email"]') || {}).value || '').trim();
    var tiktok = ((form.querySelector('[name="vr_tiktok"]') || {}).value || '').trim().replace(/^@/, '');
    var norm = normalizeOrderRef(orderRaw);

    if (norm.length < 4) { showErr(form.getAttribute('data-msg-invalid') || 'Enter your order number.'); return; }
    if (!email) { showErr(form.getAttribute('data-msg-email') || 'Enter your email.'); return; }

    var already = false;
    try { already = !!localStorage.getItem('ftf-vr-' + norm); } catch (e) { /* private mode */ }
    if (already) { showErr(form.getAttribute('data-msg-already') || 'Already requested.'); return; }

    // every order qualifies; the order total decides which reward tier.
    // when the customer is logged in their orders are on the page, so the
    // order is verified and the tier is decided for real; for guests the
    // tier is confirmed by the team.
    var rewardLabel = form.getAttribute('data-reward-unknown') || '';
    var ordersEl = modal.querySelector('[data-vr-orders]');
    if (ordersEl) {
      var orders = [];
      try { orders = JSON.parse(ordersEl.textContent); } catch (e) { orders = []; }
      var match = null;
      orders.forEach(function (o) {
        [o.name, o.number, o.id].forEach(function (key) {
          if (key !== undefined && key !== null && normalizeOrderRef(key) === norm) match = o;
        });
      });
      if (!match) { showErr(form.getAttribute('data-msg-notfound') || 'Order not found.'); return; }
      var tierCents = parseInt(form.getAttribute('data-tier-cents') || '0', 10);
      if (typeof match.total === 'number' && match.total < tierCents) {
        rewardLabel = form.getAttribute('data-reward-small') || rewardLabel;
      } else {
        rewardLabel = form.getAttribute('data-reward-big') || rewardLabel;
      }
    }

    // log the request through the store's native contact form (best effort)
    var log = modal.querySelector('[data-vr-log-form]');
    if (log) {
      var setField = function (name, value) {
        var input = log.querySelector('[name="' + name + '"]');
        if (input) input.value = value;
      };
      setField('contact[email]', email);
      setField('contact[tiktok]', tiktok ? '@' + tiktok : 'not given');
      setField('contact[order_number]', orderRaw);
      setField('contact[body]', 'Video reward request from the website.\nOrder: ' + orderRaw + '\nCheckout email: ' + email + '\nTikTok: ' + (tiktok ? '@' + tiktok : 'not given') + '\nReward tier: ' + (rewardLabel || 'unknown'));
      try { log.submit(); } catch (e) { /* logging is best-effort */ }
    }

    try { localStorage.setItem('ftf-vr-' + norm, String(Date.now())); } catch (e) { /* private mode */ }

    // flip to the instructions step
    modal.querySelectorAll('[data-vr-step]').forEach(function (step) {
      step.hidden = step.getAttribute('data-vr-step') !== 'done';
    });
    var echo = modal.querySelector('[data-vr-order-echo]');
    if (echo) echo.textContent = orderRaw;
    modal.querySelectorAll('[data-vr-reward]').forEach(function (el) {
      if (rewardLabel) el.textContent = rewardLabel;
    });
    var mailto = modal.querySelector('[data-vr-mailto]');
    if (mailto) {
      var addr = (mailto.getAttribute('href') || 'mailto:').replace(/^mailto:/, '').split('?')[0];
      var subject = 'Video reward - Order ' + orderRaw;
      var body = 'Hi! Here is my TikTok video link:\n\n[paste your video link here]\n\nOrder number: ' + orderRaw + '\nCheckout email: ' + email + '\nTikTok: ' + (tiktok ? '@' + tiktok : '') + '\nReward: ' + (rewardLabel || '');
      mailto.setAttribute('href', 'mailto:' + addr + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body));
    }
    var panel = modal.querySelector('.vr-modal__panel');
    if (panel) panel.scrollTop = 0;
  }

  /* ------------------------------------------------------------------
     Cart mutations
  ------------------------------------------------------------------ */
  function addToCart(formData, button) {
    var sectionId = getDrawerSectionId();
    if (formData instanceof FormData && sectionId) {
      formData.append('sections', sectionId);
      formData.append('sections_url', window.location.pathname);
    }
    setButtonLoading(button, true);

    return fetch(routes.cartAdd + '.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    })
      .then(function (response) {
        return response.json().then(function (json) {
          if (!response.ok || json.status) {
            var message = json.description || json.message || (config.strings && config.strings.cartError) || 'Something went wrong.';
            throw new Error(message);
          }
          return json;
        });
      })
      .then(function (json) {
        var replaced = false;
        if (json.sections && sectionId && json.sections[sectionId]) {
          replaced = replaceDrawerFromHTML(json.sections[sectionId]);
        }
        if (!replaced) {
          return refreshCartDrawer().then(function () { return json; });
        }
        return json;
      })
      .finally(function () {
        setButtonLoading(button, false);
      });
  }

  function changeCartLine(line, quantity) {
    var sectionId = getDrawerSectionId();
    var payload = { line: line, quantity: quantity };
    if (sectionId) {
      payload.sections = sectionId;
      payload.sections_url = window.location.pathname;
    }
    return fetch(routes.cartChange + '.js', Object.assign(fetchConfig(), {
      body: JSON.stringify(payload)
    }))
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var replaced = false;
        if (cart.sections && sectionId && cart.sections[sectionId]) {
          replaced = replaceDrawerFromHTML(cart.sections[sectionId]);
        }
        if (!replaced && typeof cart.total_price === 'number') {
          syncCartUI(cart.total_price, cart.item_count);
        }
        return cart;
      });
  }

  /* ------------------------------------------------------------------
     Quick view
  ------------------------------------------------------------------ */
  function getQuickView() {
    return document.getElementById('QuickViewModal');
  }

  function openQuickView(productUrl) {
    var modal = getQuickView();
    if (!modal) {
      window.location.href = productUrl;
      return;
    }
    lastFocused = document.activeElement;
    modal.classList.add('is-open');
    modal.removeAttribute('aria-hidden');
    document.body.classList.add('overflow-hidden');
    var panel = modal.querySelector('.quick-view__panel');
    if (panel) panel.focus({ preventScroll: true });

    var link = modal.querySelector('[data-quickview-product-link]');
    if (link) link.href = productUrl;

    var content = modal.querySelector('[data-quickview-content]');
    if (content) {
      content.innerHTML = '<div class="quick-view__loading"><span class="loading-screen__bar"><span class="loading-screen__bar-fill"></span></span></div>';
      var sep = productUrl.indexOf('?') === -1 ? '?' : '&';
      fetch(productUrl + sep + 'section_id=quick-view')
        .then(function (r) {
          if (!r.ok) throw new Error('quick view fetch failed');
          return r.text();
        })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var inner = doc.querySelector('[data-quick-view-product]');
          if (!inner) throw new Error('quick view markup missing');
          content.innerHTML = '';
          content.appendChild(inner);
          initVariantPickers(content);
          initImageFade(content);
          readCartAttrsAndSync();
        })
        .catch(function () {
          window.location.href = productUrl;
        });
    }
  }

  function closeQuickView() {
    var modal = getQuickView();
    if (!modal || !modal.classList.contains('is-open')) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    if (!getDrawer() || !getDrawer().classList.contains('is-open')) {
      document.body.classList.remove('overflow-hidden');
    }
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  /* ------------------------------------------------------------------
     Event delegation
  ------------------------------------------------------------------ */
  document.addEventListener('submit', function (event) {
    var form = event.target;

    if (form.matches('[data-vr-form]')) {
      event.preventDefault();
      handleVideoRewardSubmit(form);
      return;
    }

    if (form.matches('form[data-type="add-to-cart-form"], product-form form')) {
      event.preventDefault();
      var button = form.querySelector('[name="add"]');
      var errorEl = form.querySelector('.product-form__error');
      if (errorEl) { errorEl.textContent = ''; errorEl.hidden = true; }

      addToCart(new FormData(form), button)
        .then(function () {
          closeQuickView();
          if ((config.cartType || 'drawer') === 'drawer' && getDrawer()) {
            openCartDrawer();
          } else if ((config.cartType || 'drawer') !== 'drawer') {
            window.location.href = routes.cart;
          }
        })
        .catch(function (err) {
          if (errorEl) {
            errorEl.textContent = err.message;
            errorEl.hidden = false;
          } else {
            showCartError(err.message);
            if (getDrawer()) openCartDrawer();
          }
        });
    }
  });

  document.addEventListener('click', function (event) {
    var target;

    // quick view triggers (product card image/title)
    target = event.target.closest('[data-quickview-trigger]');
    if (target && getQuickView()) {
      var card = target.closest('[data-product-card]');
      var url = card ? card.getAttribute('data-product-url') : target.getAttribute('href');
      if (url && url !== '#') {
        event.preventDefault();
        openQuickView(url);
        return;
      }
    }

    target = event.target.closest('[data-quickview-close]');
    if (target) {
      event.preventDefault();
      closeQuickView();
      return;
    }

    // open cart drawer
    target = event.target.closest('[data-cart-open]');
    if (target) {
      if ((config.cartType || 'drawer') === 'drawer' && getDrawer()) {
        event.preventDefault();
        openCartDrawer();
      }
      return;
    }

    // close cart drawer
    target = event.target.closest('[data-cart-close]');
    if (target) {
      event.preventDefault();
      closeCartDrawer();
      return;
    }

    // drawer line quantity +/- and remove
    target = event.target.closest('[data-line-change]');
    if (target) {
      event.preventDefault();
      var line = parseInt(target.getAttribute('data-line'), 10);
      var qty = parseInt(target.getAttribute('data-quantity'), 10);
      var row = target.closest('.cart-item');
      if (row) row.style.opacity = '0.45';
      var isCartPage = !!target.closest('[data-cart-page]');
      changeCartLine(line, qty).then(function () {
        if (isCartPage) window.location.reload();
      });
      return;
    }

    // buy it now — express checkout for THIS item only (cart permalink),
    // so it does not check out the rest of the cart
    target = event.target.closest('[data-buy-now]');
    if (target) {
      event.preventDefault();
      var buyForm = target.closest('form') || document.getElementById(target.getAttribute('data-form-id'));
      if (!buyForm) return;
      var buyData = new FormData(buyForm);
      var variantId = buyData.get('id');
      var buyQty = parseInt(buyData.get('quantity'), 10) || 1;
      if (!variantId) return;
      setButtonLoading(target, true);
      window.location.href = '/cart/' + variantId + ':' + buyQty;
      return;
    }

    // promocode: apply
    target = event.target.closest('[data-promocode-apply]');
    if (target) {
      event.preventDefault();
      var applyScope = target.closest('.cart-drawer__footer, .cart-page__footer');
      var applyInput = applyScope ? applyScope.querySelector('[data-promocode-input]') : null;
      var code = applyInput ? applyInput.value.trim() : '';
      if (!code) return;
      setButtonLoading(target, true);
      var onCartPage = !!target.closest('[data-cart-page]');
      fetch('/discount/' + encodeURIComponent(code), { redirect: 'follow' })
        .then(function () {
          if (onCartPage) {
            window.location.reload();
            return null;
          }
          return refreshCartDrawer();
        })
        .then(function (refreshed) {
          if (refreshed === null) return;
          // the drawer was re-rendered: restore the promo UI state and report
          var freshScope = document.querySelector('.cart-drawer__footer');
          if (!freshScope) return;
          var freshInput = freshScope.querySelector('[data-promocode-input]');
          var freshStatus = freshScope.querySelector('[data-promocode-status]');
          if (freshInput) freshInput.value = code;
          if (freshStatus) {
            var applied = !!freshScope.querySelector('[data-cart-discounts]');
            freshStatus.textContent = applied
              ? ((config.strings && config.strings.promoApplied) || 'Discount applied!')
              : ((config.strings && config.strings.promoSaved) || 'Code saved — if valid, it applies at checkout.');
            freshStatus.classList.toggle('is-applied', applied);
            freshStatus.hidden = false;
          }
        })
        .catch(function () {})
        .finally(function () {
          var btn = document.querySelector('[data-promocode-apply]');
          if (btn) setButtonLoading(btn, false);
        });
      return;
    }

    // qty stepper
    target = event.target.closest('[data-qty-change]');
    if (target) {
      event.preventDefault();
      var input = document.getElementById(target.getAttribute('data-qty-target'));
      if (!input) {
        var wrap = target.closest('.qty-input');
        input = wrap ? wrap.querySelector('input') : null;
      }
      if (!input) return;
      var step = parseInt(target.getAttribute('data-qty-change'), 10) || 1;
      var next = Math.max(parseInt(input.min, 10) || 1, (parseInt(input.value, 10) || 1) + step);
      var max = parseInt(input.max, 10);
      if (max && next > max) next = max;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // announcement close
    target = event.target.closest('[data-announcement-close]');
    if (target) {
      var bar = target.closest('.announcement-bar');
      if (bar) {
        bar.classList.add('is-hidden');
        try { sessionStorage.setItem('ftf-announcement-dismissed', '1'); } catch (e) { /* private mode */ }
      }
      return;
    }

    // mobile menu
    target = event.target.closest('[data-menu-open]');
    if (target) {
      var menu = document.getElementById('MobileMenu');
      if (menu) {
        menu.classList.add('is-open');
        menu.removeAttribute('aria-hidden');
        document.body.classList.add('overflow-hidden');
      }
      return;
    }

    target = event.target.closest('[data-menu-close]');
    if (target) {
      var menuEl = document.getElementById('MobileMenu');
      if (menuEl) {
        menuEl.classList.remove('is-open');
        menuEl.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('overflow-hidden');
      }
      return;
    }

    // currency dropdown
    target = event.target.closest('[data-currency-toggle]');
    if (target) {
      var list = document.getElementById(target.getAttribute('aria-controls'));
      if (list) {
        var isOpen = !list.hidden;
        list.hidden = isOpen;
        target.setAttribute('aria-expanded', String(!isOpen));
      }
      return;
    }

    // checkout upsell
    target = event.target.closest('button[name="checkout"]');
    if (target && maybeInterceptCheckout(target, event)) return;

    target = event.target.closest('[data-upsell-add]');
    if (target) {
      addUpsellPicks(target);
      return;
    }

    target = event.target.closest('[data-upsell-skip]');
    if (target) {
      closeUpsell();
      goCheckout();
      return;
    }

    target = event.target.closest('[data-upsell-close]');
    if (target) {
      closeUpsell();
      return;
    }

    // display-currency pick
    target = event.target.closest('[data-cur-pick]');
    if (target) {
      setDisplayCurrency(target.getAttribute('data-cur-pick'));
      var pickList = target.closest('.currency-list');
      if (pickList) {
        pickList.hidden = true;
        var pickWrap = pickList.closest('.currency-pill-wrap');
        var pickToggle = pickWrap && pickWrap.querySelector('[data-currency-toggle]');
        if (pickToggle) pickToggle.setAttribute('aria-expanded', 'false');
      }
      return;
    }

    // infinite scroll manual click
    target = event.target.closest('[data-infinite-next]');
    if (target) {
      event.preventDefault();
      loadNextPage(target);
      return;
    }

    // video embed play
    target = event.target.closest('[data-video-play]');
    if (target) {
      event.preventDefault();
      var frame = target.closest('deferred-media');
      var template = frame ? frame.querySelector('template') : null;
      if (template) {
        var node = template.content.cloneNode(true);
        frame.appendChild(node);
        target.style.opacity = '0';
        target.style.pointerEvents = 'none';
        setTimeout(function () { target.remove(); }, 600);
      }
      return;
    }

    // carousel arrows
    target = event.target.closest('[data-carousel-prev], [data-carousel-next]');
    if (target) {
      var carouselEl = target.closest('[data-carousel]');
      var carouselTrack = carouselEl && carouselEl.querySelector('[data-carousel-track]');
      if (carouselTrack) {
        var dir = target.hasAttribute('data-carousel-prev') ? -1 : 1;
        carouselTrack.scrollBy({ left: dir * carouselTrack.clientWidth * 0.9, behavior: 'smooth' });
      }
      return;
    }

    // US-only gate: optional merchant-allowed dismiss
    target = event.target.closest('[data-geo-dismiss]');
    if (target) {
      dismissGeoGate();
      return;
    }

    // cart-goal free gift: add the unlocked product to the cart
    target = event.target.closest('[data-goal-add-gift]');
    if (target) {
      var giftVariant = target.getAttribute('data-gift-variant');
      if (giftVariant) {
        var giftData = new FormData();
        giftData.append('id', giftVariant);
        giftData.append('quantity', '1');
        addToCart(giftData, target).catch(function () { /* drawer shows cart errors */ });
      }
      return;
    }

    // video reward modal
    target = event.target.closest('[data-vr-open]');
    if (target) {
      openVideoReward(target.getAttribute('data-vr-target'));
      return;
    }

    target = event.target.closest('[data-vr-close]');
    if (target) {
      closeVideoReward();
      return;
    }

    // header search panel
    target = event.target.closest('[data-header-search-toggle]');
    if (target) {
      var searchPanel = document.getElementById(target.getAttribute('aria-controls') || 'HeaderSearch');
      if (searchPanel) {
        var wasHidden = searchPanel.hidden;
        searchPanel.hidden = !wasHidden;
        target.setAttribute('aria-expanded', String(wasHidden));
        if (wasHidden) {
          var searchInput = searchPanel.querySelector('[data-predictive-search-input], input[type="search"]');
          if (searchInput) {
            try { searchInput.focus({ preventScroll: true }); } catch (e) { searchInput.focus(); }
          }
        }
      }
      return;
    }

    target = event.target.closest('[data-header-search-close]');
    if (target) {
      closeHeaderSearch();
      return;
    }

    // help button: copy support email, confirm with a toast
    target = event.target.closest('[data-copy-email]');
    if (target) {
      var copyBtn = target;
      copyToClipboard(copyBtn.getAttribute('data-copy-email') || '').then(function () {
        var holder = copyBtn.closest('.header-help') || copyBtn.parentElement;
        var toast = holder ? holder.querySelector('[data-copy-toast]') : null;
        if (!toast) return;
        toast.hidden = false;
        clearTimeout(toast.ftfToastTimer);
        toast.ftfToastTimer = setTimeout(function () { toast.hidden = true; }, 2400);
      });
      return;
    }

    // close popovers when clicking outside
    var openSearchPanel = document.getElementById('HeaderSearch');
    if (openSearchPanel && !openSearchPanel.hidden) {
      var headerEl = openSearchPanel.closest('.site-header');
      if (headerEl && !headerEl.contains(event.target)) closeHeaderSearch();
    }

    document.querySelectorAll('.currency-list:not([hidden]), .sort-menu:not([hidden])').forEach(function (list) {
      var wrap = list.closest('.currency-pill-wrap, .collection-toolbar__sort');
      if (wrap && !wrap.contains(event.target)) {
        list.hidden = true;
        var toggle = document.querySelector('[aria-controls="' + list.id + '"]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      }
    });

    document.querySelectorAll('.predictive-search:not([hidden])').forEach(function (predictive) {
      var holder = predictive.closest('.search-holder') || predictive.parentElement;
      if (holder && !holder.contains(event.target)) {
        predictive.hidden = true;
      }
    });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      closeQuickView();
      closeCartDrawer();
      closeHeaderSearch();
      closeVideoReward();
      closeUpsell();
      var menuEl = document.getElementById('MobileMenu');
      if (menuEl && menuEl.classList.contains('is-open')) {
        menuEl.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
      }
      document.querySelectorAll('.currency-list:not([hidden]), .sort-menu:not([hidden])').forEach(function (l) { l.hidden = true; });
      document.querySelectorAll('.predictive-search:not([hidden])').forEach(function (l) { l.hidden = true; });
    }
  });

  document.addEventListener('click', function (event) {
    if (event.target.matches('.cart-drawer__overlay')) closeCartDrawer();
    if (event.target.matches('.quick-view__overlay')) closeQuickView();
    if (event.target.matches('.mobile-menu__overlay')) {
      var menuEl = document.getElementById('MobileMenu');
      if (menuEl) {
        menuEl.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
      }
    }
  });

  document.addEventListener('change', function (event) {
    var input = event.target.closest('[data-line-qty-input]');
    if (input) {
      var line = parseInt(input.getAttribute('data-line'), 10);
      var qty = Math.max(0, parseInt(input.value, 10) || 0);
      var isCartPage = !!input.closest('[data-cart-page]');
      changeCartLine(line, qty).then(function () {
        if (isCartPage) window.location.reload();
      });
    }

    var qtyField = event.target.closest('.qty-input__field');
    if (qtyField) {
      var maxVal = parseInt(qtyField.max, 10);
      var minVal = parseInt(qtyField.min, 10) || 1;
      var val = parseInt(qtyField.value, 10) || minVal;
      if (maxVal && val > maxVal) qtyField.value = maxVal;
      if (val < minVal) qtyField.value = minVal;
    }
  });

  /* ------------------------------------------------------------------
     Variant picker (product page + quick view)
  ------------------------------------------------------------------ */
  function initVariantPickers(scope) {
    (scope || document).querySelectorAll('[data-variant-picker]').forEach(function (picker) {
      if (picker.getAttribute('data-initialized')) return;
      picker.setAttribute('data-initialized', 'true');

      var productDataEl = document.getElementById(picker.getAttribute('data-product-json'));
      if (!productDataEl) return;
      var variants;
      try {
        variants = JSON.parse(productDataEl.textContent);
      } catch (e) {
        return;
      }

      var formIds = (picker.getAttribute('data-form-ids') || '').split(',').filter(Boolean);
      var context = picker.closest('[data-quick-view-product], .product-info') || document;

      picker.addEventListener('change', function () {
        var selected = [];
        picker.querySelectorAll('select[data-option-index]').forEach(function (sel) {
          selected[parseInt(sel.getAttribute('data-option-index'), 10)] = sel.value;
        });
        var variant = variants.find(function (v) {
          return v.options.every(function (opt, i) { return opt === selected[i]; });
        });

        formIds.forEach(function (id) {
          var form = document.getElementById(id);
          if (!form) return;
          var idInput = form.querySelector('input[name="id"]');
          var addBtn = form.querySelector('[name="add"]');
          var buyBtn = form.querySelector('[data-buy-now]') || document.querySelector('[data-buy-now][data-form-id="' + id + '"]');
          if (variant && idInput) {
            idInput.value = variant.id;
          }
          var soldOut = !variant || !variant.available;
          [addBtn, buyBtn].forEach(function (btn) {
            if (!btn) return;
            btn.disabled = soldOut;
            var label = btn.querySelector('[data-btn-label]');
            if (label) {
              if (!variant) {
                label.textContent = (config.strings && config.strings.unavailable) || 'Unavailable';
              } else if (!variant.available) {
                label.textContent = (config.strings && config.strings.soldOut) || 'Sold Out';
              } else {
                label.textContent = label.getAttribute('data-default-label');
              }
            }
          });

          // quantity cap for the selected variant
          var qtyInput = form.querySelector('input[name="quantity"]') ||
            document.querySelector('input[name="quantity"][form="' + id + '"]');
          if (qtyInput && variant) {
            if (variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny' && variant.inventory_quantity > 0) {
              qtyInput.max = variant.inventory_quantity;
              if (parseInt(qtyInput.value, 10) > variant.inventory_quantity) {
                qtyInput.value = variant.inventory_quantity;
              }
            } else {
              qtyInput.removeAttribute('max');
            }
          }
        });

        if (variant) {
          var priceWrap = context.querySelector('[data-product-price-wrap]');
          if (priceWrap) {
            var current = priceWrap.querySelector('[data-price-current]');
            var compare = priceWrap.querySelector('[data-price-compare]');
            var fire = priceWrap.querySelector('[data-price-fire]');
            var moneyFormat = config.moneyWithCurrencyFormat || config.moneyFormat;
            if (current) {
              current.setAttribute('data-price-cents', String(variant.price));
              current.removeAttribute('data-discount-base');
              current.removeAttribute('data-cur-state');
              current.textContent = formatCatalogMoney(variant.price, moneyFormat);
            }
            var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
            if (compare) {
              if (onSale) {
                compare.setAttribute('data-price-cents', String(variant.compare_at_price));
                compare.removeAttribute('data-cur-state');
                compare.textContent = formatCatalogMoney(variant.compare_at_price, moneyFormat);
              }
              compare.hidden = !onSale;
            }
            if (fire) fire.hidden = !onSale;
          }

          var flag = context.querySelector('[data-in-cart-flag]');
          if (flag) {
            flag.setAttribute('data-variant-id', variant.id);
            readCartAttrsAndSync();
          }

          if (window.history.replaceState && variant.id && !picker.closest('[data-quick-view-product]')) {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', variant.id);
            window.history.replaceState({}, '', url.toString());
          }
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Product gallery thumbnails
  ------------------------------------------------------------------ */
  function initGalleries() {
    document.querySelectorAll('[data-product-gallery]').forEach(function (gallery) {
      if (gallery.getAttribute('data-initialized')) return;
      gallery.setAttribute('data-initialized', 'true');
      var mainImg = gallery.querySelector('[data-gallery-main] img');
      gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          if (!mainImg) return;
          mainImg.classList.remove('img-loaded');
          var src = thumb.getAttribute('data-full-src');
          if (src) {
            mainImg.src = src;
            mainImg.removeAttribute('srcset');
          }
          var altText = thumb.getAttribute('data-alt');
          if (altText !== null) mainImg.alt = altText;
          if (!mainImg.complete) mainImg.classList.add('img-loading');
          gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (t) {
            t.classList.toggle('is-active', t === thumb);
          });
        });
      });
    });
  }

  /* ------------------------------------------------------------------
     Predictive search
  ------------------------------------------------------------------ */
  function initPredictiveSearch() {
    document.querySelectorAll('[data-predictive-search-input]').forEach(function (input) {
      if (input.getAttribute('data-initialized')) return;
      input.setAttribute('data-initialized', 'true');
      var wrap = input.closest('.search-field');
      if (!wrap) return;
      var dropdown = wrap.parentElement.querySelector('.predictive-search');
      if (!dropdown) return;

      var run = debounce(function () {
        var q = input.value.trim();
        if (q.length < 2) {
          dropdown.hidden = true;
          return;
        }
        fetch(routes.predictiveSearch + '.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last')
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var products = (data.resources && data.resources.results && data.resources.results.products) || [];
            if (!products.length) {
              dropdown.hidden = true;
              return;
            }
            var html = products.map(function (p) {
              var img = p.featured_image && p.featured_image.url
                ? '<img class="predictive-search__thumb" src="' + p.featured_image.url + '&width=88" alt="" loading="lazy">'
                : '<span class="predictive-search__thumb"></span>';
              var priceCents = Math.round(parseFloat(p.price) * 100);
              var priceHtml = isNaN(priceCents) ? '' : '<span class="predictive-search__price">' + formatCatalogMoney(priceCents) + '</span>';
              return '<a class="predictive-search__item" href="' + p.url + '">' + img +
                '<span class="predictive-search__title">' + p.title + '</span>' + priceHtml + '</a>';
            }).join('');
            html += '<a class="predictive-search__all" href="' + routes.search + '?q=' + encodeURIComponent(q) + '">' +
              ((config.strings && config.strings.viewAll) || 'View all results') + '</a>';
            dropdown.innerHTML = html;
            dropdown.hidden = false;
          })
          .catch(function () { dropdown.hidden = true; });
      }, 250);

      input.addEventListener('input', run);
      input.addEventListener('focus', function () {
        if (dropdown.children.length && input.value.trim().length >= 2) dropdown.hidden = false;
      });
    });
  }

  /* ------------------------------------------------------------------
     TikTok feed: theme banner with TikTok's own embed underneath.
     The third-party script loads only when the section scrolls into
     view; blocked scripts degrade to a plain link.
  ------------------------------------------------------------------ */
  function loadTikTokEmbed(box) {
    if (box.getAttribute('data-tiktok-done')) return;
    box.setAttribute('data-tiktok-done', 'true');
    var user = (box.getAttribute('data-tiktok-user') || '').replace(/[^\w.\-]/g, '');
    if (!user) return;
    var mode = box.getAttribute('data-tiktok-mode') || 'creator';
    var profile = 'https://www.tiktok.com/@' + user;
    var html = '';
    if (mode === 'videos') {
      (box.getAttribute('data-tiktok-videos') || '').split('|').forEach(function (url) {
        url = url.trim();
        var m = url.match(/^https:\/\/(?:www\.)?tiktok\.com\/@[\w.\-]+\/video\/(\d+)/);
        if (!m) return;
        html += '<blockquote class="tiktok-embed" cite="' + url + '" data-video-id="' + m[1] + '" style="max-width:325px;min-width:280px;">'
          + '<section><a target="_blank" rel="noopener" href="' + url + '">Watch on TikTok</a></section></blockquote>';
      });
    }
    if (!html) {
      html = '<blockquote class="tiktok-embed" cite="' + profile + '" data-unique-id="' + user + '" data-embed-type="creator" style="max-width:780px;min-width:288px;">'
        + '<section><a target="_blank" rel="noopener" href="' + profile + '">@' + user + ' on TikTok</a></section></blockquote>';
    }
    box.innerHTML = html;
    var s = document.createElement('script');
    s.src = 'https://www.tiktok.com/embed.js';
    s.async = true;
    document.body.appendChild(s);
  }

  function initTikTokFeed() {
    document.querySelectorAll('[data-tiktok-embed]:not([data-tiktok-init])').forEach(function (box) {
      box.setAttribute('data-tiktok-init', 'true');
      if (box.hasAttribute('data-tiktok-eager') || !('IntersectionObserver' in window)) {
        loadTikTokEmbed(box);
        return;
      }
      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          obs.disconnect();
          loadTikTokEmbed(box);
        });
      }, { rootMargin: '300px 0px' });
      obs.observe(box);
    });
  }

  /* ------------------------------------------------------------------
     Checkout upsell: once per session the checkout button first offers
     a few random sets from a merchant-picked collection. Any failure
     (bad data, network, empty collection) falls straight through to
     checkout — the modal must never block a sale.
  ------------------------------------------------------------------ */
  var upsellPicks = [];
  var upsellBusy = false;

  function upsellModal() {
    return document.getElementById('CheckoutUpsell');
  }

  function upsellDone() {
    try { return sessionStorage.getItem('ftf-upsell-done') === '1'; } catch (e) { return true; }
  }

  function markUpsellDone() {
    try { sessionStorage.setItem('ftf-upsell-done', '1'); } catch (e) { /* private mode */ }
  }

  function goCheckout() {
    window.location.href = '/checkout';
  }

  function closeUpsell() {
    var modal = upsellModal();
    if (!modal || !modal.classList.contains('is-open')) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    // the cart drawer may still be open underneath — keep scroll locked
    var drawer = document.querySelector('.cart-drawer.is-open');
    if (!drawer) document.body.classList.remove('overflow-hidden');
  }

  function maybeInterceptCheckout(button, event) {
    var modal = upsellModal();
    if (!modal || button.disabled || upsellDone()) return false;
    event.preventDefault();
    markUpsellDone(); // one shot per session, however it ends
    modal.classList.add('is-open');
    modal.removeAttribute('aria-hidden');
    document.body.classList.add('overflow-hidden');
    var panel = modal.querySelector('.upsell-modal__panel');
    if (panel) {
      try { panel.focus({ preventScroll: true }); } catch (e) { panel.focus(); }
    }
    loadUpsellPicks(modal);
    return true;
  }

  function renderUpsellPicks(wrap, addBtn) {
    wrap.innerHTML = '';
    var total = 0;
    upsellPicks.forEach(function (pick) {
      total += pick.cents;
      var row = document.createElement('div');
      row.className = 'upsell-modal__item';
      var img = document.createElement('img');
      img.className = 'upsell-modal__img';
      img.alt = '';
      if (pick.image) {
        img.src = pick.image + (pick.image.indexOf('?') === -1 ? '?width=160' : '&width=160');
      }
      var name = document.createElement('span');
      name.className = 'upsell-modal__name';
      name.textContent = pick.title;
      var price = document.createElement('span');
      price.className = 'upsell-modal__price';
      price.textContent = formatCatalogMoney(pick.cents);
      row.appendChild(img);
      row.appendChild(name);
      row.appendChild(price);
      wrap.appendChild(row);
    });
    var totalEl = addBtn && addBtn.querySelector('[data-upsell-total]');
    if (totalEl) totalEl.textContent = '· ' + formatCatalogMoney(total);
    if (addBtn) addBtn.hidden = false;
  }

  function loadUpsellPicks(modal) {
    var handle = modal.getAttribute('data-upsell-collection');
    var count = parseInt(modal.getAttribute('data-upsell-count'), 10) || 2;
    var wrap = modal.querySelector('[data-upsell-items]');
    var addBtn = modal.querySelector('[data-upsell-add]');
    var failed = false;
    var fail = function () {
      if (failed) return;
      failed = true;
      closeUpsell();
      goCheckout();
    };
    if (!handle || !wrap || typeof fetch !== 'function') { fail(); return; }
    var guard = setTimeout(fail, 6000);
    Promise.all([
      fetch(routes.cart + '.js').then(function (r) { return r.json(); }),
      fetch('/collections/' + handle + '/products.json?limit=250').then(function (r) { return r.json(); })
    ])
      .then(function (results) {
        clearTimeout(guard);
        if (failed) return;
        var inCart = {};
        ((results[0] && results[0].items) || []).forEach(function (line) { inCart[line.product_id] = true; });
        var pool = ((results[1] && results[1].products) || []).filter(function (p) {
          if (inCart[p.id]) return false;
          var v = p.variants && p.variants[0];
          return v && v.available !== false;
        });
        shuffleArray(pool);
        upsellPicks = pool.slice(0, count).map(function (p) {
          var v = p.variants[0];
          return {
            variantId: v.id,
            title: p.title,
            cents: Math.round(parseFloat(v.price) * 100),
            image: (p.images && p.images[0] && p.images[0].src) || ''
          };
        });
        if (!upsellPicks.length) { fail(); return; }
        renderUpsellPicks(wrap, addBtn);
      })
      .catch(function () {
        clearTimeout(guard);
        fail();
      });
  }

  function addUpsellPicks(button) {
    if (upsellBusy) return;
    if (!upsellPicks.length) { goCheckout(); return; }
    upsellBusy = true;
    setButtonLoading(button, true);
    var payload = {
      items: upsellPicks.map(function (pick) {
        return { id: pick.variantId, quantity: 1 };
      })
    };
    fetch(routes.cartAdd + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      // proceed either way — a failed add should never strand the buyer
      .then(function () { goCheckout(); })
      .catch(function () { goCheckout(); });
  }

  /* ------------------------------------------------------------------
     Random product order: shuffles the grid on every visit so
     overlooked items get seen. Shopify has no random sort server-side,
     so this permutes the rendered cards client-side. Promo tiles keep
     their pinned slots; an explicit ?sort_by choice wins.
  ------------------------------------------------------------------ */
  function shuffleArray(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = arr[i];
      arr[i] = arr[j];
      arr[j] = t;
    }
    return arr;
  }

  function gridCards(grid, skip) {
    return Array.prototype.slice.call(grid.children).filter(function (el) {
      return el.classList.contains('product-card') && el !== skip;
    });
  }

  function repinPromoTiles(grid) {
    Array.prototype.slice.call(grid.querySelectorAll('[data-pin-slot]')).forEach(function (tile) {
      var slot = parseInt(tile.getAttribute('data-pin-slot'), 10);
      if (!isFinite(slot)) return;
      var target = grid.children[slot];
      if (target !== tile) grid.insertBefore(tile, target || null);
    });
  }

  function shuffleRandomGrid(grid) {
    grid.setAttribute('data-shuffled', 'true');
    // remember where the merchant pinned each non-product tile (promos,
    // the collection card) so shuffling never moves it off its slot
    Array.prototype.slice.call(grid.children).forEach(function (el, i) {
      if (!el.classList.contains('product-card')) el.setAttribute('data-pin-slot', String(i));
    });
    var cards = gridCards(grid);
    if (cards.length > 1) {
      // mark each card's slot, then refill the slots with a shuffled copy
      var markers = cards.map(function (card) {
        var m = document.createComment('slot');
        grid.insertBefore(m, card);
        return m;
      });
      cards.forEach(function (card) { card.remove(); });
      var ordered = shuffleArray(cards.slice());
      markers.forEach(function (m, i) {
        grid.insertBefore(ordered[i], m);
        m.remove();
      });
      repinPromoTiles(grid);
    }
    // grids that rendered a larger candidate pool keep a random subset
    var keep = parseInt(grid.getAttribute('data-random-keep'), 10);
    if (isFinite(keep) && keep > 0) {
      var kept = 0;
      Array.prototype.slice.call(grid.children).forEach(function (el) {
        if (!el.classList.contains('product-card')) return;
        kept++;
        if (kept > keep) el.remove();
      });
    }
  }

  function initRandomOrder() {
    document.querySelectorAll('[data-random-order]:not([data-shuffled])').forEach(function (grid) {
      // an explicit sort choice wins on the collection page's own grid
      if (grid.hasAttribute('data-product-grid')) {
        try {
          if (new URLSearchParams(window.location.search).get('sort_by')) return;
        } catch (e) { /* no support: shuffle anyway */ }
      }
      shuffleRandomGrid(grid);
    });
  }

  function scatterAppendedCards(grid) {
    var fresh = Array.prototype.slice.call(grid.children).filter(function (el) {
      return el.classList.contains('product-card') && el.classList.contains('is-appended') && !el.hasAttribute('data-scattered');
    });
    fresh.forEach(function (card) {
      card.setAttribute('data-scattered', 'true');
      var cards = gridCards(grid, card);
      if (!cards.length) return;
      var j = Math.floor(Math.random() * (cards.length + 1));
      if (j >= cards.length) grid.appendChild(card);
      else grid.insertBefore(card, cards[j]);
    });
    repinPromoTiles(grid);
  }

  /* ------------------------------------------------------------------
     Price slider filter: hides loaded cards outside the picked range.
     The right end of the slider means "and up", so nothing is ever
     filtered out above the top. Appended pages get re-filtered.
  ------------------------------------------------------------------ */
  var priceFilterRange = null;

  function applyPriceFilter() {
    var grid = document.querySelector('[data-product-grid]');
    if (!grid) return;
    var shown = 0;
    Array.prototype.slice.call(grid.children).forEach(function (el) {
      if (!el.classList.contains('product-card')) return;
      var priceEl = el.querySelector('.price__current[data-price-cents]');
      var cents = priceEl ? parseInt(priceEl.getAttribute('data-price-cents'), 10) : NaN;
      var hide = false;
      if (priceFilterRange && isFinite(cents)) {
        hide = cents < priceFilterRange.lo || cents > priceFilterRange.hi;
      }
      el.classList.toggle('is-price-hidden', hide);
      if (!hide) shown++;
    });
    var note = document.querySelector('[data-price-empty]');
    if (note) note.hidden = !priceFilterRange || shown > 0;
  }

  function syncTopStripCards() {
    var strip = document.querySelector('.top-strip__row');
    if (!strip) return;
    var gridCard = document.querySelector('[data-product-grid] .product-card:not(.is-price-hidden)');
    if (!gridCard) return;
    var w = gridCard.getBoundingClientRect().width;
    // match the pinned cards to the live grid's real column width
    if (w > 50) strip.style.setProperty('--ts-card', w + 'px');
  }

  /* ------------------------------------------------------------------
     Row-fitted grids: show exactly enough cards to fill the chosen
     number of rows at the device's live column count, so no screen
     size ends on a half-empty row.
  ------------------------------------------------------------------ */
  function fitGridRows() {
    document.querySelectorAll('[data-fit-rows]').forEach(function (grid) {
      var rows = parseInt(grid.getAttribute('data-fit-rows'), 10);
      if (!isFinite(rows) || rows < 1) return;
      var tracks = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean);
      var cols = tracks.length;
      if (cols < 1) return;
      var slots = rows * cols;
      var colWidth = grid.clientWidth / cols;
      Array.prototype.slice.call(grid.children).forEach(function (el) {
        // non-product tiles (the collection card) consume their span
        if (el.classList.contains('product-card')) return;
        var w = el.getBoundingClientRect().width;
        if (w > 0 && colWidth > 0) slots -= Math.max(1, Math.round(w / colWidth));
      });
      var shown = 0;
      Array.prototype.slice.call(grid.children).forEach(function (el) {
        if (!el.classList.contains('product-card')) return;
        shown++;
        el.classList.toggle('is-row-hidden', shown > slots);
      });
    });
  }

  function initPriceFilter() {
    var wrap = document.querySelector('[data-price-filter]');
    if (!wrap || wrap.getAttribute('data-price-init')) return;
    wrap.setAttribute('data-price-init', 'true');
    var minInput = wrap.querySelector('[data-price-min]');
    var maxInput = wrap.querySelector('[data-price-max]');
    var labelEl = wrap.querySelector('[data-price-label]');
    var fillEl = wrap.querySelector('[data-price-fill]');
    if (!minInput || !maxInput) return;
    var top = parseInt(wrap.getAttribute('data-price-top'), 10) || 25;
    var applyDebounced = debounce(applyPriceFilter, 90);
    var update = function () {
      var a = parseInt(minInput.value, 10) || 0;
      var b = parseInt(maxInput.value, 10);
      if (!isFinite(b)) b = top;
      var lo = Math.min(a, b);
      var hi = Math.max(a, b);
      if (lo <= 0 && hi >= top) {
        priceFilterRange = null;
        if (labelEl) labelEl.textContent = '';
      } else {
        priceFilterRange = { lo: lo * 100, hi: hi >= top ? Infinity : hi * 100 };
        if (labelEl) labelEl.textContent = '$' + lo + ' – ' + (hi >= top ? '$' + top + '+' : '$' + hi);
      }
      if (fillEl) {
        fillEl.style.left = (lo / top * 100) + '%';
        fillEl.style.right = (100 - hi / top * 100) + '%';
      }
      applyDebounced();
    };
    minInput.addEventListener('input', update);
    maxInput.addEventListener('input', update);
    update();
  }

  /* ------------------------------------------------------------------
     Infinite scroll / load more
  ------------------------------------------------------------------ */
  var infiniteLoading = false;

  function appendNextPage(url) {
    return fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var grid = document.querySelector('[data-product-grid]');
        var newGrid = doc.querySelector('[data-product-grid]');
        if (grid && newGrid) {
          Array.prototype.slice.call(newGrid.children).forEach(function (child) {
            child.classList.add('is-appended');
            grid.appendChild(child);
          });
          if (grid.getAttribute('data-shuffled')) scatterAppendedCards(grid);
          applyPriceFilter();
          initGlowMedia();
          initImageFade(grid);
          readCartAttrsAndSync();
        }
        var nextSentinel = doc.querySelector('[data-infinite-sentinel]');
        var nextLink = doc.querySelector('[data-infinite-next]');
        return nextSentinel ? nextSentinel.getAttribute('data-next-url') : (nextLink ? nextLink.getAttribute('href') : null);
      });
  }

  function loadNextPage(link) {
    if (infiniteLoading || !link) return;
    infiniteLoading = true;
    setButtonLoading(link, true);
    appendNextPage(link.href)
      .then(function (nextUrl) {
        if (nextUrl) {
          link.href = nextUrl;
          setButtonLoading(link, false);
        } else {
          var wrapEl = link.closest('[data-infinite-wrap]');
          if (wrapEl) wrapEl.remove();
        }
      })
      .catch(function () {
        setButtonLoading(link, false);
      })
      .finally(function () {
        infiniteLoading = false;
      });
  }

  function initInfiniteScroll() {
    var sentinel = document.querySelector('[data-infinite-sentinel]');
    if (sentinel && !sentinel.getAttribute('data-observed')) {
      sentinel.setAttribute('data-observed', 'true');
      if (!('IntersectionObserver' in window)) {
        // no observer support: fall back to loading on init
        var fallbackUrl = sentinel.getAttribute('data-next-url');
        if (fallbackUrl) {
          appendNextPage(fallbackUrl).then(function (nextUrl) {
            if (nextUrl) sentinel.setAttribute('data-next-url', nextUrl);
            else sentinel.remove();
          });
        }
        return;
      }
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || infiniteLoading) return;
          var url = sentinel.getAttribute('data-next-url');
          if (!url) {
            observer.disconnect();
            sentinel.remove();
            return;
          }
          infiniteLoading = true;
          appendNextPage(url)
            .then(function (nextUrl) {
              if (nextUrl) {
                sentinel.setAttribute('data-next-url', nextUrl);
              } else {
                observer.disconnect();
                sentinel.remove();
              }
            })
            .catch(function () {})
            .finally(function () {
              infiniteLoading = false;
            });
        });
      }, { rootMargin: '700px 0px' });
      observer.observe(sentinel);
    }
  }

  /* ------------------------------------------------------------------
     Animated <details> accordions (FAQ)
  ------------------------------------------------------------------ */
  function initAnimatedDetails() {
    document.querySelectorAll('details[data-animated-details]').forEach(function (details) {
      if (details.getAttribute('data-initialized')) return;
      details.setAttribute('data-initialized', 'true');
      var summary = details.querySelector('summary');
      var content = details.querySelector('.faq-item__content');
      if (!summary || !content) return;

      var easing = 'cubic-bezier(0.22, 1, 0.36, 1)';

      function cleanup() {
        content.style.transition = '';
        content.style.height = '';
        content.style.opacity = '';
        content.style.overflow = '';
        details.removeAttribute('data-animating');
      }

      summary.addEventListener('click', function (event) {
        var duration = animMs('--anim-med');
        if (duration <= 10) return; // instant native toggle
        event.preventDefault();
        if (details.hasAttribute('data-animating')) return;
        details.setAttribute('data-animating', 'true');

        var onEnd = function (done) {
          var finished = false;
          var handler = function (e) {
            if (e.target !== content || e.propertyName !== 'height') return;
            content.removeEventListener('transitionend', handler);
            if (!finished) { finished = true; done(); }
          };
          content.addEventListener('transitionend', handler);
          // safety net if transitionend never fires
          setTimeout(function () {
            if (!finished) { finished = true; content.removeEventListener('transitionend', handler); done(); }
          }, duration + 120);
        };

        content.style.overflow = 'hidden';

        if (details.open) {
          // set explicit height first so the transition has a start value
          content.style.height = content.scrollHeight + 'px';
          content.style.opacity = '1';
          // force reflow before transitioning to 0 — avoids the jump
          void content.offsetHeight;
          content.style.transition = 'height ' + duration + 'ms ' + easing + ', opacity ' + duration + 'ms ease';
          content.style.height = '0px';
          content.style.opacity = '0';
          onEnd(function () {
            details.open = false;
            cleanup();
          });
        } else {
          // pre-collapse before revealing so there is no full-height flash
          content.style.height = '0px';
          content.style.opacity = '0';
          content.style.transition = 'none';
          details.open = true;
          var target = content.scrollHeight;
          void content.offsetHeight;
          content.style.transition = 'height ' + duration + 'ms ' + easing + ', opacity ' + duration + 'ms ease';
          content.style.height = target + 'px';
          content.style.opacity = '1';
          onEnd(cleanup);
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Hero: random floats from catalog + color-matched glows
  ------------------------------------------------------------------ */
  function computeGlowColor(img) {
    try {
      var canvas = document.createElement('canvas');
      var size = 24;
      canvas.width = size;
      canvas.height = size;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, size, size);
      var data = ctx.getImageData(0, 0, size, size).data;

      // if the corners are opaque, the image has a solid background —
      // a drop-shadow would outline the rectangle, not the item, so skip
      var corner = function (x, y) { return data[(y * size + x) * 4 + 3]; };
      var opaqueCorners = 0;
      if (corner(0, 0) > 240) opaqueCorners++;
      if (corner(size - 1, 0) > 240) opaqueCorners++;
      if (corner(0, size - 1) > 240) opaqueCorners++;
      if (corner(size - 1, size - 1) > 240) opaqueCorners++;
      if (opaqueCorners >= 3) return 'opaque';

      var r = 0, g = 0, b = 0, n = 0;
      for (var i = 0; i < data.length; i += 4) {
        var alpha = data[i + 3];
        if (alpha < 120) continue;
        var max = Math.max(data[i], data[i + 1], data[i + 2]);
        var min = Math.min(data[i], data[i + 1], data[i + 2]);
        if (max - min < 16 && max > 220) continue; // skip near-white
        r += data[i]; g += data[i + 1]; b += data[i + 2]; n++;
      }
      if (!n) return null;
      r = Math.round(r / n); g = Math.round(g / n); b = Math.round(b / n);
      // brighten towards a glow tone
      var boost = function (c) { return Math.min(255, Math.round(c * 1.25 + 30)); };
      return 'rgba(' + boost(r) + ',' + boost(g) + ',' + boost(b) + ',0.55)';
    } catch (e) {
      return null;
    }
  }

  // Product photos often ship with a near-white background baked in. A
  // drop-shadow then outlines the photo rectangle and multiply-blending
  // still leaves a faint tinted box over the hero grid. Cut the backdrop
  // away instead: flood-fill from the photo borders and clear every pixel
  // that matches the backdrop color, then swap the image for the cutout.
  function stripFloatBackground(img) {
    if (img.getAttribute('data-bg-processed')) return false;
    img.setAttribute('data-bg-processed', 'true');
    try {
      var maxSize = 640;
      var scale = Math.min(1, maxSize / Math.max(img.naturalWidth, img.naturalHeight));
      var w = Math.max(2, Math.round(img.naturalWidth * scale));
      var h = Math.max(2, Math.round(img.naturalHeight * scale));
      var canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      var ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, w, h);
      var imageData = ctx.getImageData(0, 0, w, h);
      var d = imageData.data;

      // sample the border to find the backdrop color
      var alphaSum = 0, samples = 0, r = 0, g = 0, b = 0, opaqueN = 0;
      var sample = function (x, y) {
        var p = (y * w + x) * 4;
        alphaSum += d[p + 3];
        samples++;
        if (d[p + 3] > 200) { r += d[p]; g += d[p + 1]; b += d[p + 2]; opaqueN++; }
      };
      for (var x = 0; x < w; x += 2) { sample(x, 0); sample(x, h - 1); }
      for (var y = 1; y < h - 1; y += 2) { sample(0, y); sample(w - 1, y); }
      if (!samples || alphaSum / samples < 60 || !opaqueN) return false; // already transparent
      r /= opaqueN; g /= opaqueN; b /= opaqueN;
      if (0.299 * r + 0.587 * g + 0.114 * b < 180) return false; // dark backdrop: leave the photo alone

      var TH = 42 * 42; // squared color distance treated as backdrop
      var isBg = function (p) {
        if (d[p + 3] < 40) return true;
        var dr = d[p] - r, dg = d[p + 1] - g, db = d[p + 2] - b;
        return dr * dr + dg * dg + db * db < TH;
      };

      // flood fill inward from every border pixel
      var mask = new Uint8Array(w * h);
      var stack = [];
      var seed = function (px, py) {
        var idx = py * w + px;
        if (!mask[idx] && isBg(idx * 4)) { mask[idx] = 1; stack.push(idx); }
      };
      for (x = 0; x < w; x++) { seed(x, 0); seed(x, h - 1); }
      for (y = 0; y < h; y++) { seed(0, y); seed(w - 1, y); }
      var removed = stack.length;
      while (stack.length) {
        var idx = stack.pop();
        var cx = idx % w, cy = (idx / w) | 0, ni;
        if (cx > 0) { ni = idx - 1; if (!mask[ni] && isBg(ni * 4)) { mask[ni] = 1; stack.push(ni); removed++; } }
        if (cx < w - 1) { ni = idx + 1; if (!mask[ni] && isBg(ni * 4)) { mask[ni] = 1; stack.push(ni); removed++; } }
        if (cy > 0) { ni = idx - w; if (!mask[ni] && isBg(ni * 4)) { mask[ni] = 1; stack.push(ni); removed++; } }
        if (cy < h - 1) { ni = idx + w; if (!mask[ni] && isBg(ni * 4)) { mask[ni] = 1; stack.push(ni); removed++; } }
      }
      var frac = removed / (w * h);
      if (frac < 0.04 || frac > 0.98) return false; // nothing worth cutting, or ate the whole photo

      for (var i = 0; i < w * h; i++) {
        if (mask[i]) d[i * 4 + 3] = 0;
      }
      // soften the 1px fringe the fill leaves around the item
      for (i = 0; i < w * h; i++) {
        if (mask[i]) continue;
        var fx = i % w, fy = (i / w) | 0;
        if ((fx > 0 && mask[i - 1]) || (fx < w - 1 && mask[i + 1]) || (fy > 0 && mask[i - w]) || (fy < h - 1 && mask[i + w])) {
          d[i * 4 + 3] = Math.min(d[i * 4 + 3], 140);
        }
      }
      ctx.putImageData(imageData, 0, 0);
      var url = canvas.toDataURL('image/png');
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = url;
      return true;
    } catch (e) {
      return false; // tainted canvas or similar: fall back to blend/flat handling
    }
  }

  // shared pipeline for hero floats and update-hero showcase items:
  // cut baked-in photo backdrops first, so the entrance shows the cutout
  // (no boxed-photo flash) and the glow reads the real silhouette
  function prepFloatMedia(float, img) {
    var reveal = function () {
      requestAnimationFrame(function () {
        img.classList.add('float-img-in');
        img.classList.remove('float-img-pending');
      });
    };
    var finish = function () {
      reveal();
      if (float.classList.contains('hero__float--glow')) applyGlow(float);
    };
    var prep = function () {
      if (stripFloatBackground(img)) {
        float.classList.add('hero__float--cut');
        img.addEventListener('load', finish, { once: true });
        return;
      }
      finish();
    };
    if (img.complete && img.naturalWidth) prep();
    else img.addEventListener('load', prep, { once: true });
  }

  // any element marked data-glow-media gets the full float pipeline:
  // backdrop cut, color-matched glow (when the --glow class is present)
  // and the fade-in entrance
  function initGlowMedia() {
    document.querySelectorAll('[data-glow-media]').forEach(function (el) {
      if (el.getAttribute('data-glow-init')) return;
      el.setAttribute('data-glow-init', 'true');
      var img = el.querySelector('img');
      if (!img) return;
      img.setAttribute('data-fade-bound', 'true');
      img.classList.add('float-img-pending');
      prepFloatMedia(el, img);
    });
  }

  function initUpdateHeroes() {
    document.querySelectorAll('[data-update-hero]').forEach(function (hero) {
      if (hero.getAttribute('data-update-initialized')) return;
      hero.setAttribute('data-update-initialized', 'true');
      hero.querySelectorAll('[data-update-item]').forEach(function (item, index) {
        var img = item.querySelector('img');
        if (!img) return;
        img.setAttribute('data-fade-bound', 'true');
        item.style.setProperty('--float-enter-delay', (index * 0.12) + 's');
        img.classList.add('float-img-pending');
        prepFloatMedia(item, img);
      });
    });
  }

  function applyGlow(float) {
    var img = float.querySelector('img');
    if (!img) return;
    var run = function () {
      var color = computeGlowColor(img);
      if (color === 'opaque') {
        // solid-background photo: no silhouette to glow around
        float.classList.add('hero__float--flat');
        float.style.removeProperty('--glow-color');
      } else if (color) {
        float.classList.remove('hero__float--flat');
        float.style.setProperty('--glow-color', color);
      }
    };
    if (img.complete && img.naturalWidth) run();
    else img.addEventListener('load', run, { once: true });
  }

  function initHeroFloats() {
    document.querySelectorAll('[data-hero]').forEach(function (hero) {
      if (hero.getAttribute('data-floats-initialized')) return;
      hero.setAttribute('data-floats-initialized', 'true');

      var poolEl = hero.querySelector('[data-hero-pool]');
      var randomSlots = hero.querySelectorAll('[data-float-random]');
      if (poolEl && randomSlots.length) {
        var pool = [];
        try { pool = JSON.parse(poolEl.textContent); } catch (e) { pool = []; }
        if (pool.length) {
          // shuffle
          for (var i = pool.length - 1; i > 0; i--) {
            var j = Math.floor(Math.random() * (i + 1));
            var tmp = pool[i]; pool[i] = pool[j]; pool[j] = tmp;
          }
          randomSlots.forEach(function (slot, index) {
            var pick = pool[index % pool.length];
            if (!pick) return;
            var img = slot.querySelector('img');
            var linkEl = slot.querySelector('a');
            if (img) {
              img.setAttribute('data-fade-bound', 'true'); // opt out of generic fade; floats get their own entrance
              img.crossOrigin = 'anonymous'; // before src, so the glow canvas can read pixels
              img.src = pick.src;
              img.removeAttribute('srcset');
              img.alt = pick.title || '';
            }
            if (linkEl) {
              linkEl.href = pick.url;
              linkEl.setAttribute('aria-label', pick.title || '');
            }
            // fresh random tilt on every load
            slot.style.setProperty('--float-rotate', (Math.random() * 28 - 14).toFixed(1) + 'deg');
          });
        }
      }

      // on small screens, re-lay the visible floats into deliberate slots
      // spread down both edges instead of wherever their desktop coords land
      var floats = hero.querySelectorAll('.hero__float');
      var mobileMq = window.matchMedia('(max-width: 749px)');
      function layoutFloats() {
        var slotIndex = 0;
        var mobileSlots = null;

        if (mobileMq.matches) {
          // measure the real text-free bands: beside the heading, beside the
          // CTA row, and the space under it — so placement adapts to actual
          // content instead of guessed percentages
          var heroRect = hero.getBoundingClientRect();
          var bandCenter = function (el, fallback) {
            if (!el || !heroRect.height) return fallback;
            var r = el.getBoundingClientRect();
            return ((r.top + r.height / 2 - heroRect.top) / heroRect.height) * 100;
          };
          var yHeading = bandCenter(hero.querySelector('.hero__heading'), 18);
          var ctaEl = hero.querySelector('.hero__cta-wrap') || hero.querySelector('.hero__search');
          var yCta = bandCenter(ctaEl, 66);
          var yBelow = Math.min(95, yCta + (100 - yCta) * 0.6);
          mobileSlots = [
            [7, yHeading - 3], [93, yHeading + 3],
            [5, yCta - 2], [95, yCta + 2],
            [7, yBelow], [93, yBelow - 2],
            [5, yHeading + 10], [95, yHeading + 12]
          ];
        }

        floats.forEach(function (float) {
          if (float.dataset.origX === undefined) {
            float.dataset.origX = (getComputedStyle(float).getPropertyValue('--float-x') || '').trim();
            float.dataset.origY = (getComputedStyle(float).getPropertyValue('--float-y') || '').trim();
          }
          if (float.classList.contains('hide-mobile')) return;
          if (mobileSlots) {
            var slot = mobileSlots[slotIndex % mobileSlots.length];
            slotIndex++;
            float.style.setProperty('--float-x', slot[0]);
            float.style.setProperty('--float-y', slot[1].toFixed(1));
          } else {
            float.style.setProperty('--float-x', float.dataset.origX);
            float.style.setProperty('--float-y', float.dataset.origY);
          }
        });
      }

      layoutFloats();
      window.addEventListener('load', layoutFloats, { once: true });
      if (typeof mobileMq.addEventListener === 'function') {
        mobileMq.addEventListener('change', layoutFloats);
      }

      // staggered entrance + glow for every float
      floats.forEach(function (float, index) {
        var img = float.querySelector('img');
        if (!img) return;
        img.setAttribute('data-fade-bound', 'true');
        float.style.setProperty('--float-enter-delay', (index * 0.09) + 's');
        img.classList.add('float-img-pending');

        prepFloatMedia(float, img);
      });
    });
  }

  /* ------------------------------------------------------------------
     Image fade-in on load
  ------------------------------------------------------------------ */
  function initImageFade(scope) {
    (scope || document).querySelectorAll('img:not([data-fade-bound])').forEach(function (img) {
      img.setAttribute('data-fade-bound', 'true');
      if (img.complete && img.naturalWidth) return;
      img.classList.add('img-loading');
      var done = function () {
        img.classList.remove('img-loading');
        img.classList.add('img-loaded');
      };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', function () {
        img.classList.remove('img-loading');
      }, { once: true });
    });
  }

  /* ------------------------------------------------------------------
     Product recommendations
  ------------------------------------------------------------------ */
  function initRecommendations() {
    document.querySelectorAll('[data-recommendations]').forEach(function (el) {
      if (el.getAttribute('data-loaded')) return;
      var url = el.getAttribute('data-recommendations-url');
      if (!url) return;
      el.setAttribute('data-loaded', 'true');
      fetch(url)
        .then(function (r) { return r.text(); })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var fresh = doc.querySelector('[data-recommendations]');
          if (fresh && fresh.innerHTML.trim().length) {
            el.innerHTML = fresh.innerHTML;
            initImageFade(el);
            initCarousels(el);
            readCartAttrsAndSync();
          }
        })
        .catch(function () {});
    });
  }

  /* ------------------------------------------------------------------
     Horizontal product carousels
  ------------------------------------------------------------------ */
  function updateCarouselArrows(track) {
    var carousel = track.closest('[data-carousel]');
    if (!carousel) return;
    var prev = carousel.querySelector('[data-carousel-prev]');
    var next = carousel.querySelector('[data-carousel-next]');
    var max = track.scrollWidth - track.clientWidth;
    var overflowing = max > 4;
    if (prev) {
      prev.hidden = !overflowing;
      prev.disabled = track.scrollLeft <= 4;
    }
    if (next) {
      next.hidden = !overflowing;
      next.disabled = track.scrollLeft >= max - 4;
    }
  }

  function initCarousels(scope) {
    (scope || document).querySelectorAll('[data-carousel-track]').forEach(function (track) {
      if (track.getAttribute('data-carousel-initialized')) return;
      track.setAttribute('data-carousel-initialized', 'true');
      var refresh = debounce(function () { updateCarouselArrows(track); }, 80);
      track.addEventListener('scroll', refresh);
      window.addEventListener('resize', refresh);
      updateCarouselArrows(track);
    });
  }

  /* ------------------------------------------------------------------
     Marquee duplication (seamless loop)
  ------------------------------------------------------------------ */
  function initMarquees() {
    document.querySelectorAll('.marquee__inner').forEach(function (inner) {
      if (inner.getAttribute('data-duplicated')) return;
      inner.innerHTML += inner.innerHTML;
      inner.setAttribute('data-duplicated', 'true');
    });
  }

  /* ------------------------------------------------------------------
     Flag emoji from country ISO code
  ------------------------------------------------------------------ */
  function initFlags() {
    document.querySelectorAll('[data-flag-for]').forEach(function (el) {
      var iso = (el.getAttribute('data-flag-for') || '').toUpperCase();
      if (iso.length !== 2) return;
      var flag = String.fromCodePoint(iso.charCodeAt(0) + 127397, iso.charCodeAt(1) + 127397);
      el.textContent = flag;
    });
  }

  /* ------------------------------------------------------------------
     Address country/province selectors
  ------------------------------------------------------------------ */
  function initAddressSelectors() {
    document.querySelectorAll('[data-address-country-select]').forEach(function (countrySelect) {
      if (countrySelect.getAttribute('data-initialized')) return;
      countrySelect.setAttribute('data-initialized', 'true');
      var formId = countrySelect.getAttribute('data-form-id');
      var provinceContainer = document.getElementById('AddressProvinceContainer-' + formId);
      var provinceSelect = document.getElementById('AddressProvince-' + formId);

      function updateProvinces() {
        if (!provinceContainer || !provinceSelect) return;
        var option = countrySelect.options[countrySelect.selectedIndex];
        var raw = option ? option.getAttribute('data-provinces') : null;
        var provinces = [];
        try { provinces = JSON.parse(raw || '[]'); } catch (e) { provinces = []; }
        provinceSelect.innerHTML = '';
        if (!provinces.length) {
          provinceContainer.style.display = 'none';
          return;
        }
        provinces.forEach(function (p) {
          var opt = document.createElement('option');
          opt.value = p[0];
          opt.textContent = p[1];
          provinceSelect.appendChild(opt);
        });
        var defaultProvince = provinceSelect.getAttribute('data-default');
        if (defaultProvince) provinceSelect.value = defaultProvince;
        provinceContainer.style.display = '';
      }

      var defaultCountry = countrySelect.getAttribute('data-default');
      if (defaultCountry) countrySelect.value = defaultCountry;
      countrySelect.addEventListener('change', updateProvinces);
      updateProvinces();
    });
  }

  /* ------------------------------------------------------------------
     Sticky header shadow + announcement persistence
  ------------------------------------------------------------------ */
  function initHeader() {
    var header = document.querySelector('.site-header');
    if (header) {
      // position: sticky can't work from inside Shopify's section wrapper
      // (the wrapper is exactly the header's height), so make the wrapper
      // itself sticky
      if (header.classList.contains('is-sticky')) {
        var wrap = header.closest('.shopify-section');
        if (wrap) {
          wrap.style.position = 'sticky';
          wrap.style.top = '0';
          wrap.style.zIndex = '55';
        }
      }
      var onScroll = function () {
        header.classList.toggle('is-scrolled', window.scrollY > 4);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
    try {
      if (sessionStorage.getItem('ftf-announcement-dismissed') === '1') {
        var bar = document.querySelector('.announcement-bar[data-dismissible]');
        if (bar) bar.classList.add('is-hidden');
      }
    } catch (e) { /* private mode */ }
  }

  /* ------------------------------------------------------------------
     Init
  ------------------------------------------------------------------ */
  function initAll() {
    animsOff = document.body.classList.contains('no-anims');
    initGeoGate();
    initHeader();
    initMarquees();
    initFlags();
    initPredictiveSearch();
    initVariantPickers(document);
    initGalleries();
    initCarousels(document);
    initCollectionTiles();
    initCnavAutoColors();
    initSaleTimers();
    initDiscountLinks();
    initCurrencyDisplay();
    initItemValues();
    initRecommendations();
    initAddressSelectors();
    initAnimatedDetails();
    initHeroFloats();
    initUpdateHeroes();
    initGlowMedia();
    initRandomOrder();
    fitGridRows();
    initPriceFilter();
    syncTopStripCards();
    window.addEventListener('resize', debounce(syncTopStripCards, 150));
    window.addEventListener('resize', debounce(fitGridRows, 150));
    initTikTokFeed();
    initInfiniteScroll();
    initImageFade(document);
    readCartAttrsAndSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);

  // expose a tiny API for inline scripts
  window.FTFCart = {
    open: openCartDrawer,
    close: closeCartDrawer,
    add: addToCart,
    change: changeCartLine,
    formatMoney: formatMoney,
    sync: syncCartUI,
    quickView: openQuickView
  };
})();
